import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  FiSearch, FiChevronRight, FiChevronLeft, FiFile, FiFolder,
  FiCheck, FiX, FiAlertCircle, FiFileText,
  FiChevronDown, FiFolderPlus, FiDownload,
  FiChevronsLeft, FiChevronsRight, FiExternalLink,
  FiStar, FiTrash2, FiPlus,
  FiZoomIn, FiZoomOut,
} from 'react-icons/fi';
import JSZip from 'jszip';
import { loadTable, saveRow, addRow, getTableData, isApiReady } from '../../db-viewer/data/dataAccess';
import { fetchTableRow } from '../../db-viewer/data/dbViewerService';
import { decompressBase64ToBytes } from '../utils/pdfBytes';
import { resolveDocBase64 } from '../utils/docStorage';
import { addNotification } from '../../../lib/notifications';
import SearchableDropdown, { DropdownOption } from '../../../components/ui/SearchableDropdown';
import { resolveDocuTypeId, localExtractFilingFields } from '../utils/documentExtract';
import { matchOwner, matchFolder, OwnerMatchCandidate, FolderMatchCandidate } from '../utils/filingAutoMatch';
import { interpretDate, findFormatId } from '../utils/dateDisambiguate';
import { matchPaymentMethod, matchTax } from '../utils/paymentTaxMatch';
import {
  buildContentTokens, computeMinHash, parseMinHash, computeDuplicateGroups,
  DupCandidate, DupGroupInfo, DUP_THRESHOLD,
} from '../utils/documentFingerprint';
import { fetchDedupGroups } from '../utils/dedupApi';
import apiClient from '../../../lib/api/apiClient';
import PdfCanvasViewer, { VIEW_MODES, ViewMode } from '../../../components/ui/PdfCanvasViewer';
import ManageFolders from './ManageFoldersTab';
import JournalEntriesTab from './JournalEntriesTab';
import { postJournalEntry, bankTxnToLines } from '../utils/glPosting';
import { setFormDirty, registerSaveHandler, submitSaver, confirmDiscardChanges } from '../../../lib/unsavedChanges';
import './ProcessReceiptsTab.css';

// =============================================
// Types
// =============================================
interface DocRow {
  document_id: number;
  account_owner_id: number;
  user_id: number;
  dms_folders_id: number | null;
  filename: string;
  original_filename: string;
  file_base64: string | null;
  file_size_original: number;
  mime_type: string;
  page_count: number | null;
  upload_source: string;
  upload_date: string;
  status: string;
  docu_type_id: number | null;
  owner_type: string | null;
  owner_id: number | null;
  party_name: string | null;
  invoice_ref: string | null;
  date_of_document: string | null;
  due_date: string | null;
  payment_method: string | null;
  total_amount: number | null;
  currency: string | null;
  tax_rate: number | null;
  purpose: string | null;
  notes: string | null;
  is_active: number;
  is_deleted: number;
  created_by: number | null;
  [key: string]: any;
}

interface FolderRow {
  folder_id: number;
  account_owner_id: number;
  parent_folder_id: number | null;
  folder_name: string;
  folder_path: string;
  folder_depth: number;
  folder_icon: string | null;
  folder_color: string | null;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface FolderOwnerRow {
  folder_owner_id: number;
  folder_id: number;
  owner_type: string;
  owner_id: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

// Multi-owner link for a document (dms_document_owners). One row per owner; each
// owner files the SAME stored document into their OWN folder (folder_id). Lets a
// single doc (e.g. a transfer) live in a business folder AND a personal folder.
interface DocOwnerRow {
  doc_owner_id: number;
  document_id: number;
  owner_type: string;
  owner_id: number;
  is_primary: number;
  folder_id: number | null;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

// dms_document_folders — a document filed in MANY folders, independent of owners.
interface DocFolderRow {
  doc_folder_id: number;
  document_id: number;
  folder_id: number;
  is_primary: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface OwnerEntity {
  owner_type: string;
  owner_id: number;
  display_name: string;
  badge: string;
}

interface DocuTypeRow {
  docu_type_id: number;
  docu_type_name: string;
  docu_type_code: string;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface BankTxnRow {
  txn_id: number;
  document_id: number | null;
  account_owner_id: number;
  owner_type: string | null;
  owner_id: number | null;
  txn_date: string | null;
  description: string | null;
  amount: number | null;
  direction: 'debit' | 'credit';
  balance: number | null;
  currency: string | null;
  category: string | null;
  coa_id: number | null;
  status: string;
  source: string | null;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface CoaRow {
  coa_id: number;
  account_owner_id: number;
  owner_type: string;
  owner_id: number;
  account_number: string | null;
  account_name: string;
  is_header: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

// Draft edits for a bank txn line-item (committed to acctg_bank_txn on Save).
interface TxnDraft {
  txn_id: number;
  coa_id: number | null;
  category: string;
  status: string;
}

type FilterTab = 'all' | 'to-file' | 'filed' | 'rejected' | 'bk-done';
type RightTab = 'filing' | 'bookkeeping' | 'banktxn' | 'makejournal';

// Manual statuses a user can pick in the Bank Txns grid. 'posted' is intentionally
// EXCLUDED — the only path to posted is the "Post to GL" action (which creates the
// journal entry); a row can't be marked posted without a real journal behind it.
const TXN_STATUSES = ['imported', 'reviewed', 'rejected'];

// =============================================
// Constants
// =============================================
const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'ALL' },
  { key: 'to-file', label: 'To File' },
  { key: 'filed', label: 'Filed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'bk-done', label: 'BK Done' },
];

const STATUS_MAP: Record<FilterTab, string[]> = {
  'all': [],
  'to-file': ['Review'],
  'filed': ['Filed'],
  'rejected': ['Rejected'],
  'bk-done': ['BK Done'],
};

const FOLDER_EMOJI: Record<string, string> = {
  bank: '🏦', medical: '🏥', tax: '📋', receipts: '🧾', legal: '⚖️',
  insurance: '🛡️', personal: '👤', business: '💼', education: '🎓',
  property: '🏠', investment: '📈', other: '📁',
};

const NOW_ISO = () => new Date().toISOString().replace('T', ' ').substring(0, 19);

// A folder name should "fill" the first line (break mid-token) ONLY when it holds a
// long unbreakable token — e.g. machine-generated "Sysco Regina_48598_152052", where
// space-wrapping would drop the whole long token to line 2 and leave line 1 with just
// "Sysco". Normal multi-word names (every token short) keep clean space-wrapping.
const LONG_TOKEN_LEN = 15;
function nameHasLongToken(name: string): boolean {
  return !!name && name.split(/\s+/).some(tok => tok.length >= LONG_TOKEN_LEN);
}

// Folder depth = parent depth + 1 (0 for roots). Used when re-parenting via drag-drop.
function computeFolderDepth(parentFolderId: number | null, allFolders: FolderRow[]): number {
  if (parentFolderId == null) return 0;
  const parent = allFolders.find(f => f.folder_id === parentFolderId);
  return parent ? (Number(parent.folder_depth) || 0) + 1 : 0;
}

// Materialized id-path "/1/3/" built from ancestor ids (walks parent links).
function computeFolderPath(folderId: number, allFolders: FolderRow[]): string {
  const parts: number[] = [folderId];
  let current = allFolders.find(f => f.folder_id === folderId);
  let depth = 0;
  while (current && current.parent_folder_id != null && depth < 20) {
    parts.unshift(Number(current.parent_folder_id));
    current = allFolders.find(f => f.folder_id === current!.parent_folder_id);
    depth++;
  }
  return '/' + parts.join('/') + '/';
}

// CSV cell escaping (wrap in quotes, double embedded quotes)
function csvCell(v: any): string {
  const s = v == null ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Persist UI pane-collapse preferences (Record Room + document list) so a user's
// last layout choice survives reloads. Stored as '1'/'0' under akt_ keys.
const PREF_RECORD_ROOM_OPEN = 'akt_pr_record_room_open';
const PREF_DOCLIST_OPEN = 'akt_pr_doclist_open';
const PREF_RIGHT_PANEL_OPEN = 'akt_pr_right_panel_open';

// Document-list pane width is drag-resizable. Stored in sessionStorage (NOT localStorage)
// so a widened pane persists within the session but resets to the default on a new session.
const PREF_DOCLIST_WIDTH = 'akt_pr_doclist_width';
const DOCLIST_MIN_W = 260;
const DOCLIST_MAX_W = 640;
const DOCLIST_DEFAULT_W = 320;

// Document-list sort options. Default = most recent uploads on top.
const PREF_SORT = 'akt_pr_doc_sort';
type SortKey =
  | 'recent' | 'oldest'
  | 'name-asc' | 'name-desc'
  | 'docdate-desc' | 'docdate-asc'
  | 'type' | 'party'
  | 'amount-desc' | 'amount-asc'
  | 'modified' | 'status'
  | 'duplicates';
const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'recent',       label: 'Most recent (uploaded)' },
  { value: 'oldest',       label: 'Oldest uploaded' },
  { value: 'name-asc',     label: 'Name (A–Z)' },
  { value: 'name-desc',    label: 'Name (Z–A)' },
  { value: 'docdate-desc', label: 'Document date (newest)' },
  { value: 'docdate-asc',  label: 'Document date (oldest)' },
  { value: 'type',         label: 'Type / Kind' },
  { value: 'party',        label: 'Party / Vendor' },
  { value: 'amount-desc',  label: 'Amount (high → low)' },
  { value: 'amount-asc',   label: 'Amount (low → high)' },
  { value: 'modified',     label: 'Last modified' },
  { value: 'status',       label: 'Status' },
  { value: 'duplicates',   label: 'Duplicates (grouped)' },
];
function readBoolPref(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
}
function writeBoolPref(key: string, value: boolean) {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* localStorage unavailable — ignore, preference just won't persist */
  }
}

// =============================================
// Helpers
// =============================================
function getCurrentUser(usersData: Record<string, any>[]) {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const session = JSON.parse(raw);
    const sessionId = Number(session.id || session.user_id);
    if (!sessionId) return null;
    const userRow = usersData.find((u: any) => Number(u.user_id) === sessionId);
    if (!userRow) return null;
    return {
      user_id: sessionId,
      owner_id: userRow.owner_id == null || userRow.owner_id === '' ? null : Number(userRow.owner_id),
      role: session.role || '',
      user_type_id: Number(userRow.user_type_id) || 0,
      first_name: userRow.first_name || '',
      last_name: userRow.last_name || '',
    };
  } catch { /* ignore */ }
  return null;
}

function getAccountOwner(usersData: Record<string, any>[]): Record<string, any> | null {
  return usersData.find((u: any) => u.owner_id == null || u.owner_id === '') || usersData[0] || null;
}

function getAccountOwnerForUser(userId: number, usersData: Record<string, any>[]): Record<string, any> | null {
  // eslint-disable-next-line eqeqeq
  const user = usersData.find((u: any) => u.user_id == userId);
  if (!user) return null;
  if (user.owner_id == null || user.owner_id === '') return user;
  // eslint-disable-next-line eqeqeq
  return usersData.find((u: any) => u.user_id == user.owner_id) || null;
}

function getOwnerEntities(accountOwnerId: number, usersData: Record<string, any>[], businessesData: Record<string, any>[]): OwnerEntity[] {
  const entities: OwnerEntity[] = [];

  // eslint-disable-next-line eqeqeq
  const owner = usersData.find((u: any) => u.user_id == accountOwnerId);
  if (owner) {
    entities.push({
      owner_type: 'account_owner',
      owner_id: Number(owner.user_id),
      display_name: `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email || 'Account Owner',
      badge: 'Owner',
    });
  }

  usersData
    // eslint-disable-next-line eqeqeq
    .filter((u: any) => u.owner_id == accountOwnerId && u.is_deleted !== 1 && u.relationship !== 'Personal Client')
    .forEach((u: any) => {
      entities.push({
        owner_type: 'family_member',
        owner_id: Number(u.user_id),
        display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Family Member',
        badge: 'Family',
      });
    });

  businessesData
    // eslint-disable-next-line eqeqeq
    .filter((b: any) => b.owner_id == accountOwnerId && b.is_deleted !== 1 && b.is_client_business !== 1)
    .forEach((b: any) => {
      entities.push({
        owner_type: 'business',
        owner_id: Number(b.business_id),
        display_name: b.busi_legal_name || b.busi_operating_name || 'Business',
        badge: 'Business',
      });
    });

  return entities;
}

// Build the SELECTED CLIENT's entities (the client, the client's family, the
// client's businesses) — used as the document-owner scope under Client's Accounts.
function getClientEntities(clientId: number | string, usersData: Record<string, any>[], businessesData: Record<string, any>[]): OwnerEntity[] {
  const entities: OwnerEntity[] = [];
  // eslint-disable-next-line eqeqeq
  const client = usersData.find((u: any) => u.user_id == clientId);
  const clientName = client ? (`${client.first_name || ''} ${client.last_name || ''}`.trim() || client.email || 'Client') : 'Client';
  if (client) {
    entities.push({ owner_type: 'client_personal', owner_id: Number(client.user_id), display_name: clientName, badge: 'Client' });
  }
  usersData
    // eslint-disable-next-line eqeqeq
    .filter((u: any) => u.owner_id == clientId && u.is_deleted !== 1 && u.relationship !== 'Personal Client')
    .forEach((u: any) => entities.push({
      owner_type: 'client_personal',
      owner_id: Number(u.user_id),
      display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Family',
      badge: `${clientName}'s Family`,
    }));
  businessesData
    // eslint-disable-next-line eqeqeq
    .filter((b: any) => b.owner_id == clientId && b.is_deleted !== 1)
    .forEach((b: any) => entities.push({
      owner_type: 'client_business',
      owner_id: Number(b.business_id),
      display_name: b.busi_legal_name || b.busi_operating_name || 'Business',
      badge: `${clientName}'s Biz`,
    }));
  return entities;
}

// Does a document belong to a given owner entity? Owner/family docs are keyed by
// `user_id`; businesses and clients by polymorphic `owner_type` + `owner_id`
// (mirrors the per-owner dropdown filter).
function docMatchesEntity(d: DocRow, e: OwnerEntity): boolean {
  if (e.owner_type === 'account_owner' || e.owner_type === 'family_member') {
    // eslint-disable-next-line eqeqeq
    return d.user_id == e.owner_id;
  }
  // eslint-disable-next-line eqeqeq
  return d.owner_type === e.owner_type && d.owner_id == e.owner_id;
}

function getOwnerName(ownerType: string | null, ownerId: number | null, usersData: Record<string, any>[], businessesData: Record<string, any>[]): string {
  if (!ownerType || !ownerId) return '';
  if (ownerType === 'account_owner' || ownerType === 'family_member') {
    // eslint-disable-next-line eqeqeq
    const u = usersData.find((u: any) => u.user_id == ownerId);
    if (u) return `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || '';
  }
  if (ownerType === 'business') {
    // eslint-disable-next-line eqeqeq
    const b = businessesData.find((b: any) => b.business_id == ownerId);
    if (b) return b.busi_legal_name || b.busi_operating_name || '';
  }
  return '';
}

// Owner nickname (Profile `users.nickname` / business acronym) — shown as a
// small initials badge on the left of each folder in the Record Room tree.
function getOwnerNickname(ownerType: string | null, ownerId: number | null, usersData: Record<string, any>[], businessesData: Record<string, any>[]): string {
  if (!ownerType || !ownerId) return '';
  if (ownerType === 'business' || ownerType === 'client_business') {
    // eslint-disable-next-line eqeqeq
    const b = businessesData.find((b: any) => b.business_id == ownerId);
    return b ? String(b.busi_acronym || b.acronym || b.busi_acro || '') : '';
  }
  // eslint-disable-next-line eqeqeq
  const u = usersData.find((u: any) => u.user_id == ownerId);
  return u ? String(u.nickname || '') : '';
}

// =============================================
// Main Component
// =============================================
interface ProcessReceiptsTabProps {
  // Pillar scope: 'owner' = Your Accounts (self entities), 'clients' = the picked client's entities
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
  // DMS uses this page for filing only (no bookkeeping) — hide the Bookkeeping panel.
  showBookkeeping?: boolean;
  // Folder-browser only: render JUST the Record Room folder pane (full width), hiding
  // the document list / preview / forms. Powers the DMS → Folders → "Folder List" tab,
  // reusing all the Record Room functionality (owner filter, search, tree, drag-drop,
  // context menu, New folder) without duplicating it.
  foldersOnly?: boolean;
  // When set (e.g. drilled in from the Reports dashboard), highlight + expand to this
  // folder once the tree loads.
  initialSelectedFolderId?: number;
}

const ProcessReceiptsTab: React.FC<ProcessReceiptsTabProps> = ({ viewMode = 'owner', selectedClientId = null, showBookkeeping = true, foldersOnly = false, initialSelectedFolderId }) => {
  // Reference data loaded from API
  const [usersData, setUsersData] = useState<Record<string, any>[]>([]);
  const [businessesData, setBusinessesData] = useState<Record<string, any>[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const currentUser = useMemo(() => dataLoaded ? getCurrentUser(usersData) : null, [usersData, dataLoaded]);
  const accountOwner = useMemo(() => {
    if (!currentUser) return null;
    // Resolve the account owner as the LOGGED-IN user's own account (matches how
    // UploadDocsPage stamps account_owner_id). For an owner (owner_id == null) this
    // is themselves; for a sub-user it's their owner. Using getAccountOwner() — the
    // first owner_id==null user globally — was a multi-tenancy bug: with seed/other
    // owners present (e.g. a lower-id account on staging/live), it resolved to the
    // WRONG owner, so the user's own documents were filtered out of the list.
    return getAccountOwnerForUser(currentUser.user_id, usersData);
  }, [currentUser, usersData]);
  const accountOwnerId = accountOwner ? Number(accountOwner.user_id) : 0;

  // Owner entities for the document owner dropdown — scoped to the active pillar.
  const ownerEntities = useMemo(() => {
    if (!accountOwnerId) return [];
    if (viewMode === 'clients') {
      // Client's Accounts → only the picked client's own entities.
      if (!selectedClientId) return [];
      return getClientEntities(selectedClientId, usersData, businessesData);
    }
    // Your Accounts → owner + family + owner's businesses (no clients).
    return getOwnerEntities(accountOwnerId, usersData, businessesData)
      .filter((e: any) => e.owner_type !== 'client_personal' && e.owner_type !== 'client_business');
  }, [accountOwnerId, usersData, businessesData, viewMode, selectedClientId]);

  // Folder storage tables — Client's Accounts uses the per-client folder trees
  // (dms_client_folders), Your Accounts/DMS uses the owner's own (dms_folders).
  // Folder creation is delegated to the Manage Folders popup, so this tab only
  // reads these tables (+ rename/delete existing folders).
  const FOLDERS_TABLE = viewMode === 'clients' ? 'dms_client_folders' : 'dms_folders';
  const FOLDER_OWNERS_TABLE = viewMode === 'clients' ? 'dms_client_folder_owners' : 'dms_folder_owners';

  // State
  const [documents, setDocuments] = useState<DocRow[]>([]);
  const [bankTxns, setBankTxns] = useState<BankTxnRow[]>([]);
  const [coaAccounts, setCoaAccounts] = useState<CoaRow[]>([]);
  const [glJournals, setGlJournals] = useState<Record<string, any>[]>([]);
  const [txnDrafts, setTxnDrafts] = useState<TxnDraft[]>([]);
  const [txnBusy, setTxnBusy] = useState(false);
  const [bankAccountCoaId, setBankAccountCoaId] = useState<string>('');  // offset Bank/Cash account for posting
  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [folderOwners, setFolderOwners] = useState<FolderOwnerRow[]>([]);
  const [docOwners, setDocOwners] = useState<DocOwnerRow[]>([]);
  const [docFolders, setDocFolders] = useState<DocFolderRow[]>([]);
  const [docuTypes, setDocuTypes] = useState<DocuTypeRow[]>([]);
  // Owner scope is multi-select: keys are "owner_type:owner_id". Empty = show
  // everything the current user is allowed to see. Lives in the Record Room as a
  // compact one-line trigger that opens a searchable, scrollable owner list
  // (keeps the folder tree's vertical space free for 100s of folders).
  const [selectedDocOwnerIds, setSelectedDocOwnerIds] = useState<string[]>([]);
  const [ownerPickerOpen, setOwnerPickerOpen] = useState(false);
  const [ownerFilterText, setOwnerFilterText] = useState('');
  const ownerPickerRef = useRef<HTMLDivElement>(null);
  const [selectedRecordFolderId, setSelectedRecordFolderId] = useState<number | null>(null);
  const [folderSearchTerm, setFolderSearchTerm] = useState('');
  const [folderMenuOpenId, setFolderMenuOpenId] = useState<number | null>(null);
  const folderMenuRef = useRef<HTMLDivElement>(null);
  // Inline folder actions (context menu)
  const [renamingFolderId, setRenamingFolderId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDeleteFolderId, setConfirmDeleteFolderId] = useState<number | null>(null);
  const [folderBusy, setFolderBusy] = useState(false);
  // Adding folders is delegated to the Manage Folders popup (full functionality:
  // mandatory owner selection + validation). folderRefreshKey forces a folder
  // reload after the popup closes so new folders appear in the tree.
  const [showManageFolders, setShowManageFolders] = useState(false);
  const [folderRefreshKey, setFolderRefreshKey] = useState(0);
  // Drag-drop folder move (re-parent). dropTargetId = -1 means the "root" drop zone.
  const [draggedFolderId, setDraggedFolderId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const [folderMoving, setFolderMoving] = useState(false);
  // Drag a still-unfiled document from the list onto a folder to file it.
  const [draggedDocId, setDraggedDocId] = useState<number | null>(null);
  // Per-document quick actions (operate on the selected doc)
  const [showDocMove, setShowDocMove] = useState(false);
  const [docMoveSearch, setDocMoveSearch] = useState('');
  const docMoveRef = useRef<HTMLDivElement>(null);
  // Keyboard navigation: the doc-list scroll container + the center viewer pane are
  // focusable so arrow/Tab keys drive selection and PDF scrolling.
  const docListRef = useRef<HTMLDivElement>(null);
  const centerPaneRef = useRef<HTMLDivElement>(null);
  // Default to the working queue: documents waiting to be filed, newest first.
  const [activeFilter, setActiveFilter] = useState<FilterTab>('to-file');
  const [searchTerm, setSearchTerm] = useState('');
  // Document-list sort order — persisted; defaults to most recent uploads first.
  const [sortKey, setSortKey] = useState<SortKey>(() => {
    const saved = localStorage.getItem(PREF_SORT) as SortKey | null;
    return saved && SORT_OPTIONS.some(o => o.value === saved) ? saved : 'recent';
  });
  useEffect(() => { localStorage.setItem(PREF_SORT, sortKey); }, [sortKey]);
  const [sortOpen, setSortOpen] = useState(false);
  const sortPickerRef = useRef<HTMLDivElement>(null);

  // Drag-resizable document-list width (session-scoped; resets on a new session).
  const [docListWidth, setDocListWidth] = useState<number>(() => {
    const saved = Number(sessionStorage.getItem(PREF_DOCLIST_WIDTH));
    return saved >= DOCLIST_MIN_W && saved <= DOCLIST_MAX_W ? saved : DOCLIST_DEFAULT_W;
  });
  useEffect(() => { sessionStorage.setItem(PREF_DOCLIST_WIDTH, String(docListWidth)); }, [docListWidth]);
  const onDocListResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = docListWidth;
    const onMove = (ev: MouseEvent) => {
      const w = Math.min(DOCLIST_MAX_W, Math.max(DOCLIST_MIN_W, startW + (ev.clientX - startX)));
      setDocListWidth(w);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };
  // Double-click the handle to snap back to the default width.
  const resetDocListWidth = () => setDocListWidth(DOCLIST_DEFAULT_W);
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  const [rightTab, setRightTab] = useState<RightTab>('filing');
  // Guard-then-land: switching the right-panel tab (Filing / Bookkeeping / Make Journal /
  // Bank Txns) must check for unsaved edits in the current sub-form first.
  const switchRightTab = async (next: RightTab) => {
    if (next === rightTab) return;
    if (!(await confirmDiscardChanges())) return;
    setRightTab(next);
  };
  // Record Room (folders) pane visibility — restored from the user's last choice.
  const [showRecordRoom, setShowRecordRoom] = useState(() => readBoolPref(PREF_RECORD_ROOM_OPEN, false));
  // Document-list pane can be collapsed to a thin strip to give the preview more room.
  const [showDocList, setShowDocList] = useState(() => readBoolPref(PREF_DOCLIST_OPEN, true));
  const [showRightPanel, setShowRightPanel] = useState(() => readBoolPref(PREF_RIGHT_PANEL_OPEN, true));
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  // PDF viewer controls (hosted in the merged toolbar; PdfCanvasViewer is controlled).
  const [pdfViewMode, setPdfViewMode] = useState<ViewMode>(() => {
    const s = localStorage.getItem('akt_pdf_view_mode');
    return s === 'fit' || s === 'scroll' || s === 'two' || s === 'grid' ? (s as ViewMode) : 'fit';
  });
  const [pdfZoom, setPdfZoom] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(1);
  useEffect(() => { localStorage.setItem('akt_pdf_view_mode', pdfViewMode); }, [pdfViewMode]);
  const [pdfModeMenuOpen, setPdfModeMenuOpen] = useState(false);
  const pdfModeRef = useRef<HTMLDivElement>(null);
  // Switching mode resets zoom so the fit is honored.
  const pickPdfMode = useCallback((m: ViewMode) => { setPdfViewMode(m); setPdfZoom(1); setPdfModeMenuOpen(false); }, []);
  useEffect(() => {
    if (!pdfModeMenuOpen) return;
    const h = (e: MouseEvent) => { if (pdfModeRef.current && !pdfModeRef.current.contains(e.target as Node)) setPdfModeMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [pdfModeMenuOpen]);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Remember the user's pane-collapse layout across reloads.
  useEffect(() => { writeBoolPref(PREF_RECORD_ROOM_OPEN, showRecordRoom); }, [showRecordRoom]);
  useEffect(() => { writeBoolPref(PREF_DOCLIST_OPEN, showDocList); }, [showDocList]);
  useEffect(() => { writeBoolPref(PREF_RIGHT_PANEL_OPEN, showRightPanel); }, [showRightPanel]);

  // Filing form. Owner + Folder are MULTI-select (a doc can have many owners AND be
  // filed in many folders, independent of each other). filingOwnerKeys[0] / [0] are
  // the primary on dms_documents; the rest live in dms_document_owners / dms_document_folders.
  const [filingOwnerKeys, setFilingOwnerKeys] = useState<string[]>([]);
  const [filingDocuTypeId, setFilingDocuTypeId] = useState('');
  const [filingPartyName, setFilingPartyName] = useState('');
  const [filingInvoiceRef, setFilingInvoiceRef] = useState('');
  const [filingDate, setFilingDate] = useState('');
  const [filingPaymentMethod, setFilingPaymentMethod] = useState('');
  const [filingAmount, setFilingAmount] = useState('');
  const [filingCurrency, setFilingCurrency] = useState('');
  const [filingPurpose, setFilingPurpose] = useState('');
  const [filingFilename, setFilingFilename] = useState('');   // rename field (persists in real time)
  const [filingFolderIds, setFilingFolderIds] = useState<number[]>([]);
  // Set when the owner / folder was PRE-FILLED by the document auto-matcher (vs a
  // saved value or a manual pick) — drives the "AI suggested" cue and the submit gate.
  // Part 2: payment-method / tax matching + date disambiguation + directory link.
  const [filingPaymentMethodId, setFilingPaymentMethodId] = useState('');
  const [filingTaxTypeId, setFilingTaxTypeId] = useState('');
  const [filingTaxRateId, setFilingTaxRateId] = useState('');
  const [filingTaxAmount, setFilingTaxAmount] = useState('');
  const [filingDateFormatId, setFilingDateFormatId] = useState<number | null>(null);
  const [filingRawDate, setFilingRawDate] = useState('');
  const [filingGlobalBusiId, setFilingGlobalBusiId] = useState<number | null>(null);
  // Date disambiguation: when the raw date can't be resolved from digits alone.
  const [filingDateAmbiguous, setFilingDateAmbiguous] = useState(false);
  const [filingDateCandidates, setFilingDateCandidates] = useState<{ order: string; iso: string; formatId: number | null }[]>([]);
  // Part 2 master tables (reference data; localStorage seed for account owners).
  const [paymentMethods, setPaymentMethods] = useState<Record<string, any>[]>([]);
  const [taxTypes, setTaxTypes] = useState<Record<string, any>[]>([]);
  const [taxRates, setTaxRates] = useState<Record<string, any>[]>([]);
  const [statesData, setStatesData] = useState<Record<string, any>[]>([]);
  const [locationHistory, setLocationHistory] = useState<Record<string, any>[]>([]);

  // state_id → province code (AB, BC, …) for tax labels + province filtering.
  const statesById = useMemo(() => {
    const m = new Map<number, string>();
    // eslint-disable-next-line eqeqeq
    for (const s of statesData) m.set(Number(s.state_id), String(s.state_code || '').toUpperCase());
    return m;
  }, [statesData]);

  // Address of the document's primary owner — drives which province's taxes show.
  // For a BUSINESS, the province is resolved AS OF the document's date from the
  // location history (so a doc from before a move keeps the old province), falling
  // back to the business's current address when there's no matching history period.
  const taxOwnerAddress = useMemo(() => {
    const key = filingOwnerKeys[0];
    if (!key) return { stateCode: '', country: '', hasOwner: false, asOfMoved: false };
    const [oType, oIdStr] = key.split(':');
    const oId = Number(oIdStr);
    const docDate = (filingDate || '').slice(0, 10) || new Date().toISOString().slice(0, 10);
    if (oType === 'business' || oType === 'client_business') {
      // eslint-disable-next-line eqeqeq
      const b = businessesData.find((x: any) => x.business_id == oId);
      const current = String(b?.busi_address_state || '').trim().toUpperCase();
      const country = String(b?.busi_address_country || '').trim();
      // Both own businesses and client businesses live in user_businesses, keyed by
      // business_id — so the location history resolves for either pillar.
      {
        const onDate = locationHistory.find((h: any) =>
          // eslint-disable-next-line eqeqeq
          h.business_id == oId && Number(h.is_deleted) !== 1 &&
          String(h.effective_from || '').slice(0, 10) <= docDate &&
          (!h.effective_to || String(h.effective_to).slice(0, 10) >= docDate));
        if (onDate && onDate.state_code) {
          const sc = String(onDate.state_code).trim().toUpperCase();
          return { stateCode: sc, country: String(onDate.country || country), hasOwner: !!b, asOfMoved: !!current && sc !== current };
        }
      }
      return { stateCode: current, country, hasOwner: !!b, asOfMoved: false };
    }
    // eslint-disable-next-line eqeqeq
    const u = usersData.find((x: any) => x.user_id == oId);
    return { stateCode: String(u?.address_state || '').trim().toUpperCase(), country: String(u?.address_country || '').trim(), hasOwner: !!u, asOfMoved: false };
  }, [filingOwnerKeys, usersData, businessesData, locationHistory, filingDate]);

  // Tax dropdown options, scoped to the owner's province + abbreviated labels.
  // A province shows its own rates (HST/PST/QST) PLUS federal GST — unless it's an
  // HST province (where HST replaces GST). No province → federal/country-wide only.
  const taxOptionInfo = useMemo(() => {
    // The document's date drives WHICH rate is in effect (effective_from..effective_to).
    const docDate = (filingDate || '').slice(0, 10) || new Date().toISOString().slice(0, 10);
    const effectiveOn = (r: any) => {
      const from = String(r.effective_from || '').slice(0, 10);
      const to = String(r.effective_to || '').slice(0, 10);
      if (from && from > docDate) return false;          // not yet in effect on the doc date
      if (to && to < docDate) return false;              // expired before the doc date
      return true;
    };
    const active = taxRates.filter(r => Number(r.is_deleted) !== 1 && effectiveOn(r));
    const codeOf = (id: any) => taxTypes.find(t => String(t.tax_type_id) === String(id))?.tax_code || '';
    const labelOf = (r: any) => {
      const code = codeOf(r.tax_type_id);
      const sc = r.state_id != null ? (statesById.get(Number(r.state_id)) || '') : '';
      const rate = Number(r.rate_percent);
      return [code, sc, `${rate}%`].filter(Boolean).join(' ') || r.label || `${rate}%`;
    };
    const toOpts = (rows: any[]) => rows.map(r => ({ value: String(r.tax_rate_id), label: labelOf(r) }));
    const ownerState = taxOwnerAddress.stateCode;

    const provinceRates = ownerState
      ? active.filter(r => r.state_id != null && statesById.get(Number(r.state_id)) === ownerState)
      : [];
    const ownerIsHST = provinceRates.some(r => codeOf(r.tax_type_id) === 'HST');
    const countryWide = active.filter(r => r.state_id == null && !(ownerIsHST && codeOf(r.tax_type_id) === 'GST'));

    let options: { value: string; label: string }[];
    let scopedByProvince = false;
    if (ownerState) {
      options = toOpts([...provinceRates, ...countryWide]);   // province + federal
      scopedByProvince = true;
    } else if (taxOwnerAddress.hasOwner) {
      options = toOpts(countryWide);                          // owner but no province → federal only
    } else {
      options = toOpts(active);                               // no owner picked yet → show all
    }
    return {
      options,
      scopedByProvince,
      // Prompt to add an address only once an owner is chosen but has no province.
      needsAddress: taxOwnerAddress.hasOwner && !ownerState,
      ownerState,
    };
  }, [taxRates, taxTypes, statesById, taxOwnerAddress, filingDate]);
  const [dateFormats, setDateFormats] = useState<Record<string, any>[]>([]);

  // Bookkeeping form
  const [bkOwnerKey, setBkOwnerKey] = useState('');
  const [bkDocuTypeId, setBkDocuTypeId] = useState('');
  const [bkPartyName, setBkPartyName] = useState('');
  const [bkInvoiceRef, setBkInvoiceRef] = useState('');
  const [bkDate, setBkDate] = useState('');
  const [bkDueDate, setBkDueDate] = useState('');
  const [bkAmount, setBkAmount] = useState('');
  const [bkCurrency, setBkCurrency] = useState('');
  const [bkTaxRate, setBkTaxRate] = useState('');

  // Auto-fill (transcription)
  const [extractedText, setExtractedText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [autoFillApplied, setAutoFillApplied] = useState<number | null>(null); // doc id that was auto-filled

  // Business facts extracted on open (address/phone/email/website/category) — pushed to
  // the global directory at submit (kept in a ref to avoid re-render churn).
  const filingBusinessFactsRef = useRef<Record<string, any> | null>(null);

  // =============================================
  // Owner scope (multi-select) + role gating
  // =============================================
  // Only the account owner (owner_id == null) / admin may scope across other
  // owners; a regular user (family member) is locked to their own folders/docs.
  const canPickOwners = !!currentUser && currentUser.owner_id == null;

  // The current user's own owner key (used to lock a regular user to themselves).
  const selfOwnerKey = useMemo(() => {
    if (!currentUser) return '';
    const e = ownerEntities.find((e: OwnerEntity) =>
      // eslint-disable-next-line eqeqeq
      e.owner_id == currentUser.user_id && (e.owner_type === 'account_owner' || e.owner_type === 'family_member'));
    return e ? `${e.owner_type}:${e.owner_id}` : `family_member:${currentUser.user_id}`;
  }, [currentUser, ownerEntities]);

  // Entities offered in the owner picker — all for the account owner, just self otherwise.
  const pickableOwnerEntities = useMemo(() => {
    if (canPickOwners) return ownerEntities;
    if (!currentUser) return [];
    return ownerEntities.filter((e: OwnerEntity) =>
      // eslint-disable-next-line eqeqeq
      e.owner_id == currentUser.user_id && (e.owner_type === 'account_owner' || e.owner_type === 'family_member'));
  }, [canPickOwners, ownerEntities, currentUser]);

  // The owner keys actually applied to the document list + folder tree. A
  // non-owner is ALWAYS pinned to self, regardless of the (hidden) selection.
  const scopedOwnerKeys = useMemo(
    () => canPickOwners ? selectedDocOwnerIds : (selfOwnerKey ? [selfOwnerKey] : []),
    [canPickOwners, selectedDocOwnerIds, selfOwnerKey]
  );

  // Toggle one owner in/out of the multi-select scope.
  const toggleOwner = useCallback((key: string) => {
    setSelectedDocOwnerIds(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setSelectedDocId(null);
  }, []);

  // Does a document belong to ANY of the given owner keys? (matches the
  // historical single-select semantics: account_owner → user_id, else owner_type+owner_id).
  const docMatchesOwnerKeys = useCallback((d: DocRow, keys: string[]): boolean => {
    return keys.some(key => {
      const [oType, oIdStr] = key.split(':');
      const oId = Number(oIdStr);
      // eslint-disable-next-line eqeqeq
      if (oType === 'account_owner') return d.user_id == oId;
      // eslint-disable-next-line eqeqeq
      return d.owner_type === oType && d.owner_id == oId;
    });
  }, []);

  // Active secondary-owner rows (dms_document_owners) grouped by document. Each row
  // gives an extra owner visibility of the SAME stored doc plus its own folder
  // placement — so one document surfaces under every owner & inside every folder.
  const docOwnersByDoc = useMemo(() => {
    const m = new Map<number, DocOwnerRow[]>();
    for (const r of docOwners) {
      // eslint-disable-next-line eqeqeq
      if (r.is_deleted == 1 || r.is_active == 0) continue;
      const arr = m.get(r.document_id) || [];
      arr.push(r);
      m.set(r.document_id, arr);
    }
    return m;
  }, [docOwners]);

  // Owner keys ("type:id") a document is shared with via dms_document_owners.
  const docOwnerKeySet = useCallback((d: DocRow): Set<string> =>
    new Set((docOwnersByDoc.get(d.document_id) || []).map(r => `${r.owner_type}:${r.owner_id}`)),
  [docOwnersByDoc]);

  // Owner-scope match incl. shared owners: primary (dms_documents) OR any shared row.
  const docMatchesOwnerKeysX = useCallback((d: DocRow, keys: string[]): boolean => {
    if (docMatchesOwnerKeys(d, keys)) return true;
    const shared = docOwnerKeySet(d);
    return keys.some(k => shared.has(k));
  }, [docMatchesOwnerKeys, docOwnerKeySet]);

  const docMatchesEntityX = useCallback((d: DocRow, e: OwnerEntity): boolean =>
    docMatchesEntity(d, e) || docOwnerKeySet(d).has(`${e.owner_type}:${e.owner_id}`),
  [docOwnerKeySet]);

  // Active multi-folder rows (dms_document_folders) grouped by document — the doc is
  // filed in each of these folders, independent of its owners.
  const docFoldersByDoc = useMemo(() => {
    const m = new Map<number, DocFolderRow[]>();
    for (const r of docFolders) {
      // eslint-disable-next-line eqeqeq
      if (r.is_deleted == 1 || r.is_active == 0) continue;
      const arr = m.get(r.document_id) || [];
      arr.push(r);
      m.set(r.document_id, arr);
    }
    return m;
  }, [docFolders]);

  // Folder ids a document is filed into: the primary folder (dms_documents) PLUS the
  // multi-folder rows (dms_document_folders) PLUS — for back-compat with docs filed
  // before the folders table — each shared owner's own folder.
  const docInFolderSet = useCallback((d: DocRow, folderIds: Set<number>): boolean => {
    if (d.dms_folders_id != null && folderIds.has(Number(d.dms_folders_id))) return true;
    if ((docFoldersByDoc.get(d.document_id) || []).some(r => folderIds.has(Number(r.folder_id)))) return true;
    return (docOwnersByDoc.get(d.document_id) || []).some(
      r => r.folder_id != null && folderIds.has(Number(r.folder_id))
    );
  }, [docOwnersByDoc, docFoldersByDoc]);

  // Load data from API (falls back to localStorage)
  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        const [usersRows, bizRows, docsRows, docuTypesRows, bankTxnRows, coaRows, glRows,
               payMethodRows, taxTypeRows, taxRateRows, dateFmtRows, statesRows, locationHistRows, docOwnerRows, docFolderRows] = await Promise.all([
          loadTable('users'),
          loadTable('user_businesses'),
          loadTable('dms_documents'),
          loadTable('acctg_docu_type'),
          loadTable('acctg_bank_txn'),
          loadTable('acctg_coa'),
          loadTable('acctg_gl_journal'),
          loadTable('acctg_payment_method'),
          loadTable('acctg_salestax_type'),
          loadTable('acctg_salestax_rate'),
          loadTable('site_mstr_date_formats'),
          loadTable('site_mstr_states').catch(() => [] as any[]),
          loadTable('user_business_location_history').catch(() => [] as any[]),
          // Isolated: a failure to load shared-owner rows must NOT reject the whole
          // batch (which would drop documents/folders to stale localStorage and make
          // filed docs look unfiled). Degrade to [] instead.
          loadTable('dms_document_owners').catch(() => [] as any[]),
          loadTable('dms_document_folders').catch(() => [] as any[]),
        ]);
        if (cancelled) return;
        setDocOwners(docOwnerRows as DocOwnerRow[]);
        setDocFolders(docFolderRows as DocFolderRow[]);
        setUsersData(usersRows);
        setBusinessesData(bizRows);
        setGlJournals(glRows);
        setPaymentMethods(payMethodRows);
        setTaxTypes(taxTypeRows);
        setTaxRates(taxRateRows);
        setDateFormats(dateFmtRows);
        setStatesData(statesRows);
        setLocationHistory(locationHistRows);
        // Strip file_base64 from docs to keep memory lean
        const docsWithoutBase64 = docsRows.map((d: any) => {
          const { file_base64, ...rest } = d;
          return rest;
        });
        setDocuments(docsWithoutBase64 as DocRow[]);
        setDocuTypes(docuTypesRows as DocuTypeRow[]);
        setBankTxns(bankTxnRows as BankTxnRow[]);
        setCoaAccounts(coaRows as CoaRow[]);
        setDataLoaded(true);
      } catch {
        // Fallback to localStorage if API fails
        if (cancelled) return;
        setUsersData(getTableData('users'));
        setBusinessesData(getTableData('user_businesses'));
        const docs = getTableData('dms_documents', { omit: ['file_base64'] }) as DocRow[];
        setDocuments(docs);
        setDocuTypes(getTableData('acctg_docu_type') as DocuTypeRow[]);
        setBankTxns(getTableData('acctg_bank_txn') as BankTxnRow[]);
        setCoaAccounts(getTableData('acctg_coa') as CoaRow[]);
        setGlJournals(getTableData('acctg_gl_journal'));
        setPaymentMethods(getTableData('acctg_payment_method'));
        setTaxTypes(getTableData('acctg_salestax_type'));
        setTaxRates(getTableData('acctg_salestax_rate'));
        setDateFormats(getTableData('site_mstr_date_formats'));
        setStatesData(getTableData('site_mstr_states'));
        setLocationHistory(getTableData('user_business_location_history'));
        setDocOwners(getTableData('dms_document_owners') as DocOwnerRow[]);
        setDocFolders(getTableData('dms_document_folders') as DocFolderRow[]);
        setDataLoaded(true);
      }

      // Check if navigated here from Upload page with a specific doc
      const navDocId = sessionStorage.getItem('akt_navigate_to_doc');
      if (navDocId) {
        sessionStorage.removeItem('akt_navigate_to_doc');
        const id = Number(navDocId);
        if (!isNaN(id)) setSelectedDocId(id);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, []);

  // Load the folder trees — re-runs when the storage table changes (i.e. when the
  // pillar switches between Your Accounts and Client's Accounts, or the client changes).
  useEffect(() => {
    let cancelled = false;
    async function fetchFolders() {
      try {
        const [foldersRows, folderOwnersRows] = await Promise.all([
          loadTable(FOLDERS_TABLE),
          loadTable(FOLDER_OWNERS_TABLE),
        ]);
        if (cancelled) return;
        setFolders(foldersRows as FolderRow[]);
        setFolderOwners(folderOwnersRows as FolderOwnerRow[]);
      } catch {
        if (cancelled) return;
        setFolders(getTableData(FOLDERS_TABLE) as FolderRow[]);
        setFolderOwners(getTableData(FOLDER_OWNERS_TABLE) as FolderOwnerRow[]);
      }
    }
    fetchFolders();
    return () => { cancelled = true; };
  }, [FOLDERS_TABLE, FOLDER_OWNERS_TABLE, folderRefreshKey]);

  // Close the document Move popover on outside click
  useEffect(() => {
    if (!showDocMove) return;
    const handler = (e: MouseEvent) => {
      if (docMoveRef.current && !docMoveRef.current.contains(e.target as Node)) {
        setShowDocMove(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDocMove]);

  // Close the owner picker popover on outside click
  useEffect(() => {
    if (!ownerPickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (ownerPickerRef.current && !ownerPickerRef.current.contains(e.target as Node)) {
        setOwnerPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ownerPickerOpen]);

  useEffect(() => {
    if (!sortOpen) return;
    const handler = (e: MouseEvent) => {
      if (sortPickerRef.current && !sortPickerRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [sortOpen]);

  // Close folder context menu on outside click
  useEffect(() => {
    if (folderMenuOpenId === null) return;
    const handler = (e: MouseEvent) => {
      if (folderMenuRef.current && !folderMenuRef.current.contains(e.target as Node)) {
        setFolderMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [folderMenuOpenId]);

  // Clear success message after a few seconds
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(''), 3000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // =============================================
  // Folder descendant helper (for recursive folder filtering)
  // =============================================
  const getFolderAndDescendantIds = useCallback((folderId: number): Set<number> => {
    const ids = new Set<number>([folderId]);
    const activeFolders = folders.filter(f => f.is_deleted !== 1 && f.is_active !== 0);
    const queue = [folderId];
    while (queue.length > 0) {
      const parentId = queue.shift()!;
      activeFolders.forEach(f => {
        if (f.parent_folder_id === parentId && !ids.has(f.folder_id)) {
          ids.add(f.folder_id);
          queue.push(f.folder_id);
        }
      });
    }
    return ids;
  }, [folders]);

  // =============================================
  // Folder actions (Record Room context menu)
  // =============================================
  // Notify the account owner when a non-owner mutates a shared folder.
  const notifyOwnerOfFolderChange = useCallback((summary: string) => {
    if (!currentUser || currentUser.user_id === accountOwnerId) return;
    addNotification({
      account_owner_id: accountOwnerId,
      type: 'folder_changed',
      title: 'Folder updated',
      message: `${currentUser.first_name} ${summary}`,
      actor_user_id: currentUser.user_id,
      actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
    });
  }, [currentUser, accountOwnerId]);

  // --- Add folder: delegate to the Manage Folders popup ---
  // Folder creation (root or sub-folder) goes through the full Manage Folders UI
  // so owner selection + validation are enforced (the old inline add allowed a
  // sub-folder with no owner). On close we reload the tree to show new folders.
  const openManageFolders = useCallback(() => {
    setFolderMenuOpenId(null);
    setRenamingFolderId(null);
    setConfirmDeleteFolderId(null);
    setShowManageFolders(true);
  }, []);

  const closeManageFolders = useCallback(() => {
    setShowManageFolders(false);
    setFolderRefreshKey(k => k + 1);   // re-fetch folders so new ones appear
  }, []);

  // --- Rename ---
  const startRename = useCallback((folder: FolderRow) => {
    setFolderMenuOpenId(null);
    setConfirmDeleteFolderId(null);
    setRenamingFolderId(folder.folder_id);
    setRenameValue(folder.folder_name);
  }, []);

  const submitRename = useCallback(async () => {
    const id = renamingFolderId;
    const name = renameValue.trim();
    if (!id || !name) return;
    const target = folders.find(f => f.folder_id === id);
    if (target && name === target.folder_name) { setRenamingFolderId(null); return; }
    const dup = folders.some(f =>
      f.folder_id !== id && f.parent_folder_id === (target?.parent_folder_id ?? null) &&
      f.is_deleted !== 1 && f.folder_name.trim().toLowerCase() === name.toLowerCase()
    );
    if (dup) { setSuccessMsg(`A folder named "${name}" already exists here.`); return; }

    setFolderBusy(true);
    try {
      await saveRow(FOLDERS_TABLE, id, { folder_name: name, updated_at: NOW_ISO() }, 'folder_id');
      setFolders(prev => prev.map(f => f.folder_id === id ? { ...f, folder_name: name } : f));
      setRenamingFolderId(null);
      notifyOwnerOfFolderChange(`renamed a folder to "${name}"`);
      setSuccessMsg('Folder renamed.');
    } catch {
      setSuccessMsg('Could not rename the folder. Please try again.');
    } finally {
      setFolderBusy(false);
    }
  }, [renamingFolderId, renameValue, folders, FOLDERS_TABLE, notifyOwnerOfFolderChange]);

  // --- Delete (soft-delete folder + sub-folders, keeps documents) ---
  const startDeleteFolder = useCallback((folderId: number) => {
    setFolderMenuOpenId(null);
    setRenamingFolderId(null);
    // Safety: only an EMPTY folder (no documents in it or any sub-folder) may be
    // deleted. If it still holds documents, surface them in the list pane and block.
    const ids = getFolderAndDescendantIds(folderId);
    const docCount = documents.filter(d =>
      // eslint-disable-next-line eqeqeq
      d.is_deleted != 1 && d.is_active == 1 &&
      d.dms_folders_id != null && ids.has(d.dms_folders_id)
    ).length;
    if (docCount > 0) {
      const name = folders.find(f => f.folder_id === folderId)?.folder_name || 'folder';
      setActiveFilter('all');
      setSelectedRecordFolderId(folderId);   // doc pane now lists this folder's docs (incl. sub-folders)
      setSelectedDocId(null);
      setConfirmDeleteFolderId(null);
      setSuccessMsg(`"${name}" still has ${docCount} document${docCount === 1 ? '' : 's'} — empty it first (move or reject them), then delete.`);
      return;
    }
    setConfirmDeleteFolderId(folderId);
  }, [getFolderAndDescendantIds, documents, folders]);

  const confirmDeleteFolder = useCallback(async () => {
    const folderId = confirmDeleteFolderId;
    if (!folderId || !currentUser) return;
    setFolderBusy(true);
    const now = NOW_ISO();
    const ids = Array.from(getFolderAndDescendantIds(folderId));
    const folderName = folders.find(f => f.folder_id === folderId)?.folder_name || 'folder';
    try {
      for (const id of ids) {
        await saveRow(FOLDERS_TABLE, id, { is_deleted: 1, updated_at: now }, 'folder_id');
        const owners = folderOwners.filter(o => o.folder_id === id && o.is_deleted !== 1);
        for (const o of owners) {
          await saveRow(FOLDER_OWNERS_TABLE, o.folder_owner_id, { is_deleted: 1, updated_at: now }, 'folder_owner_id');
        }
      }
      const idSet = new Set(ids);
      setFolders(prev => prev.map(f => idSet.has(f.folder_id) ? { ...f, is_deleted: 1 } : f));
      setFolderOwners(prev => prev.map(o => idSet.has(o.folder_id) ? { ...o, is_deleted: 1 } : o));
      if (selectedRecordFolderId !== null && idSet.has(selectedRecordFolderId)) setSelectedRecordFolderId(null);
      setConfirmDeleteFolderId(null);
      notifyOwnerOfFolderChange(`deleted folder "${folderName}"`);
      setSuccessMsg(`Folder "${folderName}" deleted.`);
    } catch {
      setSuccessMsg('Could not delete the folder. Please try again.');
    } finally {
      setFolderBusy(false);
    }
  }, [confirmDeleteFolderId, currentUser, getFolderAndDescendantIds, folders, folderOwners, selectedRecordFolderId, FOLDERS_TABLE, FOLDER_OWNERS_TABLE, notifyOwnerOfFolderChange]);

  // Stable "owner signature" of a folder (sorted "type:id" keys of its active owners).
  // Two folders with the same signature belong to the same owner(s).
  const folderOwnerKey = useCallback((folderId: number): string => {
    return folderOwners
      .filter(o => o.folder_id === folderId && o.is_deleted !== 1)
      .map(o => `${o.owner_type}:${o.owner_id}`)
      .sort()
      .join('|');
  }, [folderOwners]);

  // A folder may be dropped onto a target ONLY when the target is an umbrella folder
  // (umbrellas exist to group folders across owners) OR shares the dragged folder's
  // exact owner set. This blocks moving one user's folder under another user's folder.
  const canDropFolderInto = useCallback((draggedId: number, targetId: number): boolean => {
    const target = folders.find(f => f.folder_id === targetId);
    if (!target) return false;
    if (Number(target.umbrella_folder) === 1) return true;        // umbrella → any owner welcome
    return folderOwnerKey(draggedId) === folderOwnerKey(targetId); // else: same owner only
  }, [folders, folderOwnerKey]);

  // --- Move folder (drag-drop re-parent) — maintains the parent/child owner rule ---
  // Re-parents the folder, recomputes depth/path for its whole subtree, and (when
  // dropped under a NON-umbrella parent) re-owns the subtree to the parent's owner(s).
  const moveFolder = useCallback(async (folderId: number, newParentId: number | null) => {
    const moved = folders.find(f => f.folder_id === folderId);
    if (!moved || !currentUser) return;
    // eslint-disable-next-line eqeqeq
    if (moved.parent_folder_id == newParentId) return;                 // no change
    const subtree = getFolderAndDescendantIds(folderId);
    if (newParentId != null && subtree.has(newParentId)) {
      setSuccessMsg("Can't move a folder into itself or one of its sub-folders.");
      return;
    }
    // Owner guard: only same-owner targets (or umbrella folders) accept the drop —
    // a folder owned by one user can't be moved under a different user's folder.
    if (newParentId != null && !canDropFolderInto(folderId, newParentId)) {
      setSuccessMsg(`"${moved.folder_name}" can only be moved under an umbrella folder or a folder with the same owner.`);
      setDraggedFolderId(null);
      setDropTargetId(null);
      return;
    }
    const newParent = newParentId != null ? folders.find(f => f.folder_id === newParentId) : null;
    const parentNonUmbrella = !!newParent && Number(newParent.umbrella_folder) !== 1;
    const parentOwners = parentNonUmbrella
      ? folderOwners.filter(o => o.folder_id === newParent!.folder_id && o.is_deleted !== 1)
      : null;

    setFolderMoving(true);
    const now = NOW_ISO();
    const ids = Array.from(subtree);                                   // BFS order, self first
    try {
      // 1. Re-parent the dragged folder
      await saveRow(FOLDERS_TABLE, folderId, { parent_folder_id: newParentId, umbrella_folder: 0, updated_at: now }, 'folder_id');
      // 2. Recompute depth + path for the whole subtree (parents before children)
      const local = folders.map(f => f.folder_id === folderId ? { ...f, parent_folder_id: newParentId } : { ...f });
      for (const id of ids) {
        const node = local.find(f => f.folder_id === id);
        if (!node) continue;
        const depth = computeFolderDepth(node.parent_folder_id, local);
        const path = computeFolderPath(id, local);
        node.folder_depth = depth;                                     // children read the updated parent depth
        await saveRow(FOLDERS_TABLE, id, { folder_depth: depth, folder_path: path, updated_at: now }, 'folder_id');
      }
      // 3. Owner inheritance: a non-umbrella parent forces its owner(s) onto the subtree
      if (parentOwners) {
        for (const id of ids) {
          const existing = folderOwners.filter(o => o.folder_id === id && o.is_deleted !== 1);
          for (const o of existing) {
            await saveRow(FOLDER_OWNERS_TABLE, o.folder_owner_id, { is_deleted: 1, updated_at: now }, 'folder_owner_id');
          }
          for (let i = 0; i < parentOwners.length; i++) {
            const po = parentOwners[i];
            await addRow(FOLDER_OWNERS_TABLE, {
              folder_id: id, owner_type: po.owner_type, owner_id: po.owner_id,
              is_primary: i === 0 ? 1 : 0, access_level: 'manage',
              is_active: 1, is_deleted: 0, added_by: currentUser.user_id,
              created_at: now, updated_at: now,
            }, 'folder_owner_id');
          }
        }
      }
      setFolderRefreshKey(k => k + 1);                                 // re-fetch the tree
      notifyOwnerOfFolderChange(`moved folder "${moved.folder_name}"`);
      setSuccessMsg(`Moved "${moved.folder_name}".`);
    } catch {
      setSuccessMsg('Could not move the folder. Please try again.');
    } finally {
      setFolderMoving(false);
      setDraggedFolderId(null);
      setDropTargetId(null);
    }
  }, [folders, folderOwners, currentUser, getFolderAndDescendantIds, canDropFolderInto, FOLDERS_TABLE, FOLDER_OWNERS_TABLE, notifyOwnerOfFolderChange]);

  // =============================================
  // Drag a still-unfiled document onto a folder to file it.
  // Rules: only documents NOT yet in a folder; the document's owner must match the
  // folder's owner — UNLESS the document has no owner assigned (raw), which may be
  // dropped into any folder. (Defined before the folder drag handlers, which depend
  // on these for combined folder-or-document drop handling.)
  // =============================================
  const canDropDocIntoFolder = useCallback((docId: number, folderId: number): boolean => {
    const doc = documents.find(d => d.document_id === docId);
    if (!doc) return false;
    if (doc.dms_folders_id != null) return false;                    // already filed
    if (!(doc.owner_type && doc.owner_id)) return true;              // raw → any folder
    const docKey = `${doc.owner_type}:${doc.owner_id}`;
    return folderOwners
      .filter(o => o.folder_id === folderId && o.is_deleted !== 1)
      .some(o => `${o.owner_type}:${o.owner_id}` === docKey);
  }, [documents, folderOwners]);

  const moveDocToFolder = useCallback(async (docId: number, folderId: number) => {
    if (!canDropDocIntoFolder(docId, folderId)) return;
    const doc = documents.find(d => d.document_id === docId);
    const folder = folders.find(f => f.folder_id === folderId);
    const updates: Record<string, any> = { dms_folders_id: folderId, updated_at: NOW_ISO() };

    // A still-raw document (no owner) inherits the folder's primary owner on drop —
    // it's no longer raw afterward. (Umbrella folders carry no owner → nothing to
    // inherit; the doc stays owner-less but is now filed.) For person owners the
    // owner_id IS a user_id, so mirror it into user_id to keep owner-scoping correct.
    const wasRaw = !(doc?.owner_type && doc?.owner_id);
    let assignedName = '';
    if (wasRaw) {
      const fOwners = folderOwners.filter(o => o.folder_id === folderId && o.is_deleted !== 1);
      const primary = fOwners.find(o => Number(o.is_primary) === 1) || fOwners[0];
      if (primary) {
        updates.owner_type = primary.owner_type;
        updates.owner_id = primary.owner_id;
        if (primary.owner_type === 'account_owner' || primary.owner_type === 'family_member') {
          updates.user_id = primary.owner_id;
        }
        assignedName = getOwnerName(primary.owner_type, primary.owner_id, usersData, businessesData);
      }
    }

    try {
      await saveRow('dms_documents', docId, updates, 'document_id');
    } catch { /* dataAccess handles localStorage fallback */ }
    setDocuments(prev => prev.map(d => d.document_id === docId ? { ...d, ...updates } : d));
    setSuccessMsg(assignedName
      ? `Filed "${doc?.filename || 'document'}" into "${folder?.folder_name || 'folder'}" — owner set to ${assignedName}.`
      : `Filed "${doc?.filename || 'document'}" into "${folder?.folder_name || 'folder'}".`);
  }, [canDropDocIntoFolder, documents, folders, folderOwners, usersData, businessesData]);

  const handleFolderDragStart = useCallback((e: React.DragEvent, folderId: number) => {
    setDraggedFolderId(folderId);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(folderId)); } catch { /* ignore */ }
  }, []);
  const handleFolderDragOver = useCallback((e: React.DragEvent, folderId: number) => {
    // Document drag (filing an unfiled doc into a folder) takes precedence.
    if (draggedDocId != null) {
      if (canDropDocIntoFolder(draggedDocId, folderId)) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropTargetId(prev => (prev === folderId ? prev : folderId));
      } else {
        e.dataTransfer.dropEffect = 'none';
        setDropTargetId(prev => (prev === folderId ? null : prev));
      }
      return;
    }
    if (draggedFolderId == null || draggedFolderId === folderId) return;
    // Invalid target (different owner, non-umbrella) → show a no-drop cursor and
    // don't highlight it. Not calling preventDefault means the drop won't fire here.
    if (!canDropFolderInto(draggedFolderId, folderId)) {
      e.dataTransfer.dropEffect = 'none';
      setDropTargetId(prev => (prev === folderId ? null : prev));
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetId(prev => (prev === folderId ? prev : folderId));
  }, [draggedFolderId, draggedDocId, canDropFolderInto, canDropDocIntoFolder]);
  const handleFolderDrop = useCallback((e: React.DragEvent, folderId: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedDocId != null) {
      const did = draggedDocId;
      setDropTargetId(null);
      setDraggedDocId(null);
      moveDocToFolder(did, folderId);
      return;
    }
    const src = draggedFolderId;
    setDropTargetId(null);
    setDraggedFolderId(null);
    if (src != null && src !== folderId) moveFolder(src, folderId);
  }, [draggedFolderId, draggedDocId, moveFolder, moveDocToFolder]);
  const handleFolderDragEnd = useCallback(() => { setDraggedFolderId(null); setDropTargetId(null); }, []);

  // --- Export folder document list to CSV ---
  const handleExportCsv = useCallback((folder: FolderRow) => {
    setFolderMenuOpenId(null);
    const folderIds = getFolderAndDescendantIds(folder.folder_id);
    const docs = documents.filter(d =>
      d.is_deleted !== 1 && d.is_active !== 0 &&
      d.dms_folders_id != null && folderIds.has(d.dms_folders_id)
    );
    if (docs.length === 0) {
      setSuccessMsg('No documents in this folder to export.');
      return;
    }
    const docuTypeName = (id: number | null) =>
      id == null ? '' : (docuTypes.find(dt => dt.docu_type_id === id)?.docu_type_name || '');
    const headers = ['Document ID', 'Filename', 'Owner', 'Document Type', 'Party Name', 'Invoice/Ref', 'Document Date', 'Amount', 'Currency', 'Status', 'Upload Date'];
    const lines = [headers.map(csvCell).join(',')];
    docs.forEach(d => {
      lines.push([
        d.document_id,
        d.filename || d.original_filename || '',
        getOwnerName(d.owner_type, d.owner_id, usersData, businessesData),
        docuTypeName(d.docu_type_id),
        d.party_name || '',
        d.invoice_ref || '',
        d.date_of_document || '',
        d.total_amount != null ? d.total_amount : '',
        d.currency || '',
        d.status || '',
        d.upload_date || '',
      ].map(csvCell).join(','));
    });
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${folder.folder_name.replace(/[^a-zA-Z0-9_-]/g, '_')}_documents.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSuccessMsg(`${docs.length} document(s) exported to CSV.`);
  }, [documents, getFolderAndDescendantIds, docuTypes, usersData, businessesData]);

  // --- Push to Google Drive (integration pending) ---
  const handlePushToDrive = useCallback(() => {
    setFolderMenuOpenId(null);
    setSuccessMsg('Google Drive sync is coming soon — your files stay safe here in the meantime.');
  }, []);

  // =============================================
  // Filtered documents
  // =============================================
  // ── Duplicate detection ──────────────────────────────────────────────────
  // Cluster this account owner's documents (within the active pillar + owner
  // scope) into duplicate groups, independent of the status/folder/search filters
  // so a doc's "duplicate" flag is stable wherever it appears. New uploads carry a
  // stored content_minhash + file_hash (migration 087); legacy docs fall back to a
  // field-based signature computed here so they still participate.
  // Local (fallback) clustering — used in localStorage / offline mode, or if the
  // server endpoint is unreachable. Scoped to the active pillar + owner scope.
  const computeLocalDupInfo = useCallback((): Map<number, DupGroupInfo> => {
    let docs = documents.filter(d => d.is_deleted !== 1 && d.is_active === 1);
    // eslint-disable-next-line eqeqeq
    docs = docs.filter(d => d.account_owner_id == accountOwnerId);
    if (viewMode === 'clients') {
      docs = docs.filter(d => ownerEntities.some(e => docMatchesEntityX(d, e)));
    } else {
      docs = docs.filter(d => d.owner_type !== 'client_personal' && d.owner_type !== 'client_business');
    }
    if (scopedOwnerKeys.length > 0) {
      docs = docs.filter(d => docMatchesOwnerKeysX(d, scopedOwnerKeys));
    }
    // Guard against an O(n²) blow-up on huge accounts.
    if (docs.length < 2 || docs.length > 1500) return new Map();

    const candidates: DupCandidate[] = docs.map(d => {
      let sig = parseMinHash(d.content_minhash);
      if (!sig) {
        // Legacy/no-text doc → derive a signature from the stored Filing fields.
        const toks = buildContentTokens({
          partyName: d.party_name,
          totalAmount: d.total_amount,
          dateOfDocument: d.date_of_document,
          invoiceRef: d.invoice_ref,
          currency: d.currency,
        });
        sig = toks.length ? computeMinHash(toks) : null;
      }
      return {
        id: d.document_id,
        createdAt: d.created_at || d.upload_date,
        fileHash: d.file_hash || null,
        sig,
      };
    });
    return computeDuplicateGroups(candidates, DUP_THRESHOLD) as Map<number, DupGroupInfo>;
  }, [documents, accountOwnerId, viewMode, ownerEntities, scopedOwnerKeys, docMatchesEntityX, docMatchesOwnerKeysX]);

  // Duplicate groups — prefer the SERVER (it clusters from the persisted fingerprint
  // columns only, never reading the document blobs), falling back to local
  // computation when there's no API/session. Recomputes when the doc set changes.
  const [dupInfo, setDupInfo] = useState<Map<number, DupGroupInfo>>(new Map());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isApiReady()) {
        const serverGroups = await fetchDedupGroups();
        if (!cancelled && serverGroups) { setDupInfo(serverGroups); return; }
      }
      if (!cancelled) setDupInfo(computeLocalDupInfo());
    })();
    return () => { cancelled = true; };
  }, [computeLocalDupInfo]);

  const filteredDocs = useMemo(() => {
    let docs = documents.filter(d => d.is_deleted !== 1 && d.is_active === 1);
    // Filter by account owner (the manager)
    // eslint-disable-next-line eqeqeq
    docs = docs.filter(d => d.account_owner_id == accountOwnerId);

    // Scope to the active pillar: Client's Accounts shows ONLY the picked client's
    // documents; Your Accounts excludes client-owned docs.
    if (viewMode === 'clients') {
      docs = docs.filter(d => ownerEntities.some(e => docMatchesEntityX(d, e)));
    } else {
      docs = docs.filter(d => d.owner_type !== 'client_personal' && d.owner_type !== 'client_business');
    }

    // Filter by selected owner scope (multi-select; non-owner is pinned to self)
    if (scopedOwnerKeys.length > 0) {
      docs = docs.filter(d => docMatchesOwnerKeysX(d, scopedOwnerKeys));
    }

    // Filter by selected Record Room folder (includes subfolders). A doc matches if
    // its primary folder OR any shared owner's folder is in the subtree.
    if (selectedRecordFolderId !== null) {
      const folderIds = getFolderAndDescendantIds(selectedRecordFolderId);
      docs = docs.filter(d => docInFolderSet(d, folderIds));
    }

    // Filter by status tab. The "Duplicates" sort ignores the status tab so the
    // full group (original + every copy) shows together regardless of where each
    // copy is in its workflow.
    const statuses = STATUS_MAP[activeFilter];
    if (statuses.length > 0 && sortKey !== 'duplicates') {
      docs = docs.filter(d => statuses.includes(d.status));
    }

    // Search
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      docs = docs.filter(d =>
        d.filename?.toLowerCase().includes(q) ||
        d.original_filename?.toLowerCase().includes(q)
      );
    }

    // Sort (default: most recent uploads on top)
    const ts = (s?: string | null) => { if (!s) return 0; const t = Date.parse(s); return isNaN(t) ? 0 : t; };
    const nameOf = (d: DocRow) => (d.original_filename || d.filename || '').toLowerCase();
    const typeOf = (d: DocRow) => (docuTypes.find(dt => dt.docu_type_id === d.docu_type_id)?.docu_type_name || '').toLowerCase();
    const amt = (d: DocRow) => (d.total_amount == null ? 0 : Number(d.total_amount));
    const byRecent = (a: DocRow, b: DocRow) => ts(b.created_at) - ts(a.created_at);

    // "Duplicates" sort: keep only docs that belong to a duplicate group, then lay
    // out group-by-group — original first, copies after it (highest match first).
    // Groups are ordered by their original's recency.
    if (sortKey === 'duplicates') {
      const dupDocs = docs.filter(d => dupInfo.has(d.document_id));
      const groupRecency = (originalId: number) => {
        const orig = docs.find(d => d.document_id === originalId);
        return orig ? ts(orig.created_at) : 0;
      };
      return dupDocs.sort((a, b) => {
        const ia = dupInfo.get(a.document_id)!;
        const ib = dupInfo.get(b.document_id)!;
        if (ia.groupId !== ib.groupId) {
          // newer original group first, stable by group id
          const r = groupRecency(ib.groupId as number) - groupRecency(ia.groupId as number);
          return r !== 0 ? r : Number(ia.groupId) - Number(ib.groupId);
        }
        // within a group: original on top, then by match % desc, then recency
        if (ia.isOriginal !== ib.isOriginal) return ia.isOriginal ? -1 : 1;
        return (ib.matchPct - ia.matchPct) || byRecent(a, b);
      });
    }

    const sorted = [...docs];
    switch (sortKey) {
      case 'oldest':       sorted.sort((a, b) => ts(a.created_at) - ts(b.created_at)); break;
      case 'name-asc':     sorted.sort((a, b) => nameOf(a).localeCompare(nameOf(b))); break;
      case 'name-desc':    sorted.sort((a, b) => nameOf(b).localeCompare(nameOf(a))); break;
      case 'docdate-desc': sorted.sort((a, b) => ts(b.date_of_document) - ts(a.date_of_document) || byRecent(a, b)); break;
      case 'docdate-asc':  sorted.sort((a, b) => ts(a.date_of_document) - ts(b.date_of_document) || byRecent(a, b)); break;
      case 'type':         sorted.sort((a, b) => typeOf(a).localeCompare(typeOf(b)) || nameOf(a).localeCompare(nameOf(b))); break;
      case 'party':        sorted.sort((a, b) => (a.party_name || '').toLowerCase().localeCompare((b.party_name || '').toLowerCase()) || byRecent(a, b)); break;
      case 'amount-desc':  sorted.sort((a, b) => amt(b) - amt(a)); break;
      case 'amount-asc':   sorted.sort((a, b) => amt(a) - amt(b)); break;
      case 'modified':     sorted.sort((a, b) => ts(b.updated_at) - ts(a.updated_at)); break;
      case 'status':       sorted.sort((a, b) => (a.status || '').localeCompare(b.status || '') || byRecent(a, b)); break;
      case 'recent':
      default:             sorted.sort(byRecent); break;
    }
    return sorted;
  }, [documents, accountOwnerId, viewMode, ownerEntities, scopedOwnerKeys, docMatchesOwnerKeysX, docMatchesEntityX, docInFolderSet, selectedRecordFolderId, getFolderAndDescendantIds, activeFilter, searchTerm, sortKey, docuTypes, dupInfo]);

  // Selected document
  const selectedDoc = useMemo(
    () => documents.find(d => d.document_id === selectedDocId) || null,
    [documents, selectedDocId]
  );

  // Current index for navigation
  const currentIndex = useMemo(
    () => selectedDocId ? filteredDocs.findIndex(d => d.document_id === selectedDocId) : -1,
    [filteredDocs, selectedDocId]
  );

  // Counts per filter
  const filterCounts = useMemo(() => {
    // eslint-disable-next-line eqeqeq
    let docs = documents.filter(d => d.is_deleted != 1 && d.is_active == 1);
    // eslint-disable-next-line eqeqeq
    docs = docs.filter(d => d.account_owner_id == accountOwnerId);
    // Same pillar scoping as the document list.
    if (viewMode === 'clients') {
      docs = docs.filter(d => ownerEntities.some(e => docMatchesEntityX(d, e)));
    } else {
      docs = docs.filter(d => d.owner_type !== 'client_personal' && d.owner_type !== 'client_business');
    }
    if (scopedOwnerKeys.length > 0) {
      docs = docs.filter(d => docMatchesOwnerKeysX(d, scopedOwnerKeys));
    }
    if (selectedRecordFolderId !== null) {
      const folderIds = getFolderAndDescendantIds(selectedRecordFolderId);
      docs = docs.filter(d => docInFolderSet(d, folderIds));
    }
    return {
      all: docs.length,
      'to-file': docs.filter(d => d.status === 'Review').length,
      filed: docs.filter(d => d.status === 'Filed').length,
      rejected: docs.filter(d => d.status === 'Rejected').length,
      'bk-done': docs.filter(d => d.status === 'BK Done').length,
    };
  }, [documents, accountOwnerId, viewMode, ownerEntities, scopedOwnerKeys, docMatchesOwnerKeysX, docMatchesEntityX, docInFolderSet, selectedRecordFolderId, getFolderAndDescendantIds]);

  // When the last "To File" document is PROCESSED (filed / rejected / BK-done) and the
  // queue is now empty, hand the workflow over to the "Rejected" tab. Gated on an
  // action flag (set by the processing handlers) so it fires only on real processing —
  // never on initial load or when owner/folder scope changes leave To File empty.
  const justProcessedRef = useRef(false);
  const selectFirstRejectedRef = useRef(false);
  useEffect(() => {
    if (!justProcessedRef.current) return;
    justProcessedRef.current = false;
    if (filterCounts['to-file'] === 0 && activeFilter === 'to-file') {
      setActiveFilter('rejected');
      selectFirstRejectedRef.current = true;   // pick the first Rejected doc once the list reflows
    }
  }, [filterCounts, activeFilter]);

  // After the To File → Rejected hand-off, auto-select the first Rejected document
  // (runs once filteredDocs has switched to the Rejected list).
  useEffect(() => {
    if (!selectFirstRejectedRef.current || activeFilter !== 'rejected') return;
    selectFirstRejectedRef.current = false;
    setSelectedDocId(filteredDocs.length > 0 ? filteredDocs[0].document_id : null);
  }, [filteredDocs, activeFilter]);

  // Persist a document rename to PostgreSQL (filename column) and reflect it in the
  // in-memory list immediately. Used by the Filing tab's "Rename document" field —
  // saved in real time (debounced while typing + on blur), no Submit needed.
  const renameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistRename = useCallback(async (docId: number, rawName: string) => {
    const name = rawName.trim();
    if (!name) return;                                  // ignore empty — keep last good name
    const current = documents.find(d => d.document_id === docId);
    if (current && current.filename === name) return;   // nothing changed
    try {
      const now = NOW_ISO();
      await saveRow('dms_documents', docId, { filename: name, updated_at: now }, 'document_id');
      // saveRow (API mode) doesn't touch the local cache — update state directly so the
      // doc list / viewer title reflect the new name without a full reload.
      setDocuments(prev => prev.map(d => d.document_id === docId ? { ...d, filename: name, updated_at: now } : d));
    } catch {
      // best-effort; a real failure leaves the field showing the typed value (the
      // next blur/submit retries). Renaming never blocks the user.
    }
  }, [documents]);

  // Fetch file_base64 on-demand for a single document (not held in state to keep
  // memory lean). Fetch ONLY that row — pulling the whole dms_documents table just to
  // read one doc's bytes returns multiple MB (every doc's base64), blows the
  // localStorage cache quota, and was leaving the viewer blank. Single-row API first,
  // then the local cache as a fallback.
  const getDocBase64 = useCallback(async (docId: number): Promise<string | null> => {
    let row: any = null;
    if (isApiReady()) {
      try { row = await fetchTableRow('dms_documents', docId); } catch { /* fall through to local cache */ }
    }
    if (!row) {
      const all = getTableData('dms_documents');
      row = all.find((d: any) => d.document_id === docId) || null;
    }
    // Prefer the blob store (file_path) and fall back to inline base64; legacy
    // rows migrate into the blob store on first read.
    return resolveDocBase64(row);
  }, []);

  // Load PDF bytes when selecting a document
  useEffect(() => {
    if (!selectedDoc) {
      setPdfBytes(null);
      return;
    }
    let cancelled = false;
    setPdfLoading(true);
    getDocBase64(selectedDoc.document_id)
      .then(base64 => {
        if (cancelled || !base64) {
          if (!cancelled) setPdfBytes(null);
          return;
        }
        return decompressBase64ToBytes(base64).then(bytes => {
          if (!cancelled) setPdfBytes(bytes);
        });
      })
      .catch(() => { if (!cancelled) setPdfBytes(null); })
      .finally(() => { if (!cancelled) setPdfLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDoc, getDocBase64]);

  // Pre-fill right panel forms when selecting a document
  useEffect(() => {
    if (!selectedDoc) {
      // Nothing selected (e.g. the last doc was just deleted) — blank both forms
      // so they don't keep showing the previous document's data.
      setFilingOwnerKeys([]);
      setFilingDocuTypeId('');
      setFilingPartyName('');
      setFilingInvoiceRef('');
      setFilingDate('');
      setFilingPaymentMethod('');
      setFilingAmount('');
      setFilingCurrency('');
      setFilingPurpose('');
      setFilingFilename('');
      setFilingFolderIds([]);
      setFilingPaymentMethodId('');
      setFilingTaxTypeId('');
      setFilingTaxRateId('');
      setFilingTaxAmount('');
      setFilingDateFormatId(null);
      setFilingRawDate('');
      setFilingGlobalBusiId(null);
      setFilingDateAmbiguous(false);
      setFilingDateCandidates([]);
      filingBusinessFactsRef.current = null;
      setBkOwnerKey('');
      setBkDocuTypeId('');
      setBkPartyName('');
      setBkInvoiceRef('');
      setBkDate('');
      setBkDueDate('');
      setBkAmount('');
      setBkCurrency('');
      setBkTaxRate('');
      setExtractedText('');
      setAutoFillApplied(null);
      setErrorMsg('');
      setFormDirty(false);
      return;
    }
    // Pre-fill filing form from saved data first
    const ownerKey = selectedDoc.owner_type && selectedDoc.owner_id
      ? `${selectedDoc.owner_type}:${selectedDoc.owner_id}`
      : '';
    // Owners (multi): primary on dms_documents + every active dms_document_owners row.
    const extraOwnerKeys = docOwners
      .filter(r => r.document_id === selectedDoc.document_id && Number(r.is_deleted) !== 1 && Number(r.is_active) !== 0)
      .map(r => `${r.owner_type}:${r.owner_id}`);
    setFilingOwnerKeys(Array.from(new Set([ownerKey, ...extraOwnerKeys].filter(Boolean))));
    setFilingDocuTypeId(selectedDoc.docu_type_id ? String(selectedDoc.docu_type_id) : '');
    setFilingPartyName(selectedDoc.party_name || '');
    setFilingInvoiceRef(selectedDoc.invoice_ref || '');
    setFilingDate(selectedDoc.date_of_document || '');
    setFilingPaymentMethod(selectedDoc.payment_method || '');
    setFilingAmount(selectedDoc.total_amount != null ? String(selectedDoc.total_amount) : '');
    setFilingCurrency(selectedDoc.currency || '');
    setFilingPurpose(selectedDoc.purpose || '');
    setFilingFilename(selectedDoc.filename || '');
    // Folders (multi): primary on dms_documents + every active dms_document_folders row
    // + (back-compat) any folder carried on the doc's owner rows.
    const primaryFolder = selectedDoc.dms_folders_id ? Number(selectedDoc.dms_folders_id) : null;
    const extraFolderIds = docFolders
      .filter(r => r.document_id === selectedDoc.document_id && Number(r.is_deleted) !== 1 && Number(r.is_active) !== 0)
      .map(r => Number(r.folder_id));
    const legacyOwnerFolderIds = docOwners
      .filter(r => r.document_id === selectedDoc.document_id && Number(r.is_deleted) !== 1 && Number(r.is_active) !== 0 && r.folder_id != null)
      .map(r => Number(r.folder_id));
    setFilingFolderIds(Array.from(new Set([
      ...(primaryFolder != null ? [primaryFolder] : []),
      ...extraFolderIds, ...legacyOwnerFolderIds,
    ])));
    // Part 2 saved values
    setFilingPaymentMethodId(selectedDoc.payment_method_id ? String(selectedDoc.payment_method_id) : '');
    setFilingTaxTypeId(selectedDoc.tax_type_id ? String(selectedDoc.tax_type_id) : '');
    setFilingTaxRateId(selectedDoc.tax_rate_id ? String(selectedDoc.tax_rate_id) : '');
    setFilingTaxAmount(selectedDoc.tax_amount != null ? String(selectedDoc.tax_amount) : '');
    setFilingDateFormatId(selectedDoc.date_format_id || null);
    setFilingRawDate(selectedDoc.raw_date_string || '');
    setFilingGlobalBusiId(selectedDoc.global_busi_id || null);
    setFilingDateAmbiguous(false);
    setFilingDateCandidates([]);
    filingBusinessFactsRef.current = null;

    // Pre-fill bookkeeping form
    setBkOwnerKey(ownerKey);
    setBkDocuTypeId(selectedDoc.docu_type_id ? String(selectedDoc.docu_type_id) : '');
    setBkPartyName(selectedDoc.party_name || '');
    setBkInvoiceRef(selectedDoc.invoice_ref || '');
    setBkDate(selectedDoc.date_of_document || '');
    setBkDueDate(selectedDoc.due_date || '');
    setBkAmount(selectedDoc.total_amount != null ? String(selectedDoc.total_amount) : '');
    setBkCurrency(selectedDoc.currency || '');
    setBkTaxRate(selectedDoc.tax_rate != null ? String(selectedDoc.tax_rate) : '');

    // Reset extracted text + auto-match cues (the saved values above are not "AI suggested")
    setExtractedText('');
    setAutoFillApplied(null);
    setErrorMsg('');
  }, [selectedDoc, docOwners, docFolders]);

  // Auto-extract text from PDF and auto-fill empty fields
  useEffect(() => {
    if (!selectedDoc) return;
    // Don't re-extract if already done for this doc
    if (autoFillApplied === selectedDoc.document_id) return;

    let cancelled = false;
    setExtracting(true);

    getDocBase64(selectedDoc.document_id).then(base64 => {
      if (cancelled || !base64) {
        if (!cancelled) setExtracting(false);
        return;
      }

    return localExtractFilingFields(base64, selectedDoc.filename || selectedDoc.original_filename || '')
      .then((parsed) => {
        if (cancelled) return;
        setExtractedText(parsed.rawText || '');

        // Auto-fill ONLY empty fields (don't overwrite saved data)
        // Filing form
        setFilingPartyName(prev => prev || parsed.partyName);
        setFilingInvoiceRef(prev => prev || parsed.invoiceRef);
        setFilingDate(prev => prev || parsed.dateOfDocument);
        setFilingAmount(prev => prev || parsed.totalAmount);
        setFilingCurrency(prev => prev || parsed.currency);
        setFilingPaymentMethod(prev => prev || parsed.paymentMethod);
        setFilingPurpose(prev => prev || parsed.purpose);

        // Auto-match document type by hint — synonym-aware (e.g. "Bill" → Purchase
        // Invoice), falling back to "Other Document", so the required Nature field
        // gets a sensible value instead of staying blank on an exact-name miss.
        if (!selectedDoc.docu_type_id && parsed.docTypeHint) {
          const dtId = resolveDocuTypeId(parsed.docTypeHint, docuTypes);
          if (dtId != null) {
            setFilingDocuTypeId(prev => prev || String(dtId));
            setBkDocuTypeId(prev => prev || String(dtId));
          }
        }

        // Bookkeeping form
        setBkPartyName(prev => prev || parsed.partyName);
        setBkInvoiceRef(prev => prev || parsed.invoiceRef);
        setBkDate(prev => prev || parsed.dateOfDocument);
        setBkAmount(prev => prev || parsed.totalAmount);
        setBkCurrency(prev => prev || parsed.currency);

        // Conservative owner/folder auto-match (Part 1). Owner is only SUGGESTED when
        // the document itself names an account entity (otherwise left blank — the
        // standing "uploader ≠ owner" rule). A folder match is suggested independently
        // but does NOT drag in an owner from the folder: when the folder is AI-suggested
        // and no owner was named, the submit gate makes the user confirm the owner.
        const matchText = `${parsed.rawText || ''} ${selectedDoc.filename || ''} ${selectedDoc.original_filename || ''}`;
        const ownerSug = !selectedDoc.owner_type
          ? matchOwner(matchText, ownerMatchCandidates)
          : null;
        if (ownerSug) {
          setFilingOwnerKeys(prev => prev.length ? prev : [ownerSug]);   // a saved/just-set owner wins
        }
        if (!selectedDoc.dms_folders_id) {
          const folderSug = matchFolder(matchText, folderMatchCandidates, ownerSug);
          if (folderSug != null) {
            setFilingFolderIds(prev => prev.length ? prev : [folderSug]);   // already filed / set
          }
        }

        // Capture the OTHER party's PUBLIC business facts for the directory (pushed at submit).
        filingBusinessFactsRef.current = {
          address_1: parsed.businessAddress || '',
          website: parsed.businessWebsite || '',
          general_phone: parsed.businessPhone || '',
          general_email: parsed.businessEmail || '',
          category_code: parsed.businessCategory || '',
        };

        // Part 2: payment method, tax, and date disambiguation (empty fields only).
        if (!selectedDoc.payment_method_id) {
          const pmId = matchPaymentMethod(matchText, paymentMethods as any);
          if (pmId) setFilingPaymentMethodId(prev => prev || String(pmId));
        }
        if (!selectedDoc.tax_rate_id) {
          // Resolve the owner's province so same-% rates (e.g. 15% HST in NB/NL/PE)
          // disambiguate to the owner's jurisdiction.
          let ownerStateId: number | null = null;
          {
            const ot = selectedDoc.owner_type;
            let sc = '';
            if (ot === 'business' || ot === 'client_business') {
              // eslint-disable-next-line eqeqeq
              const b = businessesData.find((x: any) => x.business_id == selectedDoc.owner_id);
              sc = String(b?.busi_address_state || '').trim().toUpperCase();
            } else {
              // eslint-disable-next-line eqeqeq
              const u = usersData.find((x: any) => x.user_id == (selectedDoc.owner_id || selectedDoc.user_id));
              sc = String(u?.address_state || '').trim().toUpperCase();
            }
            // eslint-disable-next-line eqeqeq
            const st = sc ? statesData.find((s: any) => String(s.state_code || '').toUpperCase() === sc) : null;
            if (st) ownerStateId = Number(st.state_id);
          }
          // Only rates in effect on the document's date are candidates (effective-dated).
          const docDate = String(selectedDoc.date_of_document || '').slice(0, 10) || new Date().toISOString().slice(0, 10);
          const ratesOnDate = (taxRates as any[]).filter(r => {
            const from = String(r.effective_from || '').slice(0, 10);
            const to = String(r.effective_to || '').slice(0, 10);
            return !(from && from > docDate) && !(to && to < docDate);
          });
          const tax = matchTax({
            text: parsed.rawText || '', types: taxTypes as any, rates: ratesOnDate,
            ratePercent: parsed.taxRatePercent ? Number(parsed.taxRatePercent) : null,
            taxAmount: parsed.taxAmount ? Number(parsed.taxAmount) : null,
            stateId: ownerStateId,
          });
          if (tax.tax_rate_id) setFilingTaxRateId(prev => prev || String(tax.tax_rate_id));
          if (tax.tax_type_id) setFilingTaxTypeId(prev => prev || String(tax.tax_type_id));
          if (tax.tax_amount != null) setFilingTaxAmount(prev => prev || String(tax.tax_amount));
        }
        // Date: interpret the raw printed token (party learned format → self → ambiguous).
        const rawDate = parsed.rawDateString || '';
        if (rawDate && !selectedDoc.date_of_document) {
          setFilingRawDate(prev => prev || rawDate);
          const di = interpretDate(rawDate, { formats: dateFormats as any, partyFormatId: selectedDoc.date_format_id || null });
          if (di.ambiguous) {
            setFilingDateAmbiguous(true);
            setFilingDateCandidates(di.candidates.map(c => ({
              order: c.order, iso: c.iso, formatId: findFormatId(dateFormats as any, c.order as any),
            })));
          } else if (di.iso) {
            setFilingDate(di.iso);                       // principled read overrides the naive parser guess
            if (di.formatId) setFilingDateFormatId(di.formatId);
          }
        }

        setAutoFillApplied(selectedDoc.document_id);
        setExtracting(false);
      })
      .catch(() => {
        if (!cancelled) setExtracting(false);
      });
    }).catch(() => {
      if (!cancelled) setExtracting(false);
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoc?.document_id, getDocBase64]);

  // =============================================
  // Navigation
  // =============================================
  const navigateDoc = useCallback((direction: -1 | 1) => {
    const newIdx = currentIndex + direction;
    if (newIdx >= 0 && newIdx < filteredDocs.length) {
      setSelectedDocId(filteredDocs[newIdx].document_id);
    }
  }, [currentIndex, filteredDocs]);

  // Keyboard: the document LIST pane. ↑/↓ move the selection (= scroll through the
  // list); Tab hands control to the middle document viewer.
  const handleListKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); navigateDoc(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); navigateDoc(-1); }
    else if (e.key === 'Tab' && !e.shiftKey && centerPaneRef.current) {
      e.preventDefault();
      centerPaneRef.current.focus();
    }
  }, [navigateDoc]);

  // Keyboard: the middle VIEWER pane. ←/→ step documents; ↑/↓ + PgUp/PgDn (+ Home/End)
  // scroll the open document.
  const handleViewerKeyDown = useCallback((e: React.KeyboardEvent) => {
    const scroller = centerPaneRef.current?.querySelector('.pcv-pages') as HTMLElement | null;
    // In the viewer's "Scroll" (actual-size) mode, ←/→ pan the page horizontally;
    // in every other mode they step documents (page fits, no horizontal scroll).
    const panHorizontally = (typeof localStorage !== 'undefined' && localStorage.getItem('akt_pdf_view_mode') === 'scroll');
    switch (e.key) {
      case 'ArrowRight':
        if (panHorizontally && scroller) { e.preventDefault(); scroller.scrollBy({ left: 120 }); }
        else { e.preventDefault(); navigateDoc(1); }
        break;
      case 'ArrowLeft':
        if (panHorizontally && scroller) { e.preventDefault(); scroller.scrollBy({ left: -120 }); }
        else { e.preventDefault(); navigateDoc(-1); }
        break;
      case 'ArrowDown':  if (scroller) { e.preventDefault(); scroller.scrollBy({ top: 90 }); } break;
      case 'ArrowUp':    if (scroller) { e.preventDefault(); scroller.scrollBy({ top: -90 }); } break;
      case 'PageDown':   if (scroller) { e.preventDefault(); scroller.scrollBy({ top: scroller.clientHeight * 0.9 }); } break;
      case 'PageUp':     if (scroller) { e.preventDefault(); scroller.scrollBy({ top: -scroller.clientHeight * 0.9 }); } break;
      case 'Home':       if (scroller) { e.preventDefault(); scroller.scrollTo({ top: 0 }); } break;
      case 'End':        if (scroller) { e.preventDefault(); scroller.scrollTo({ top: scroller.scrollHeight }); } break;
      default: break;
    }
  }, [navigateDoc]);

  // Keep the keyboard-selected row visible in the list.
  useEffect(() => {
    if (!selectedDocId || !docListRef.current) return;
    const el = docListRef.current.querySelector(`[data-doc-id="${selectedDocId}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedDocId]);

  // On launch, move control to the first document in the list (the To File queue by
  // default) so the viewer + Filing form are populated and the arrows work right away.
  // One-shot; respects a doc opened via navigation (Upload/Reports bridge).
  const didInitialSelectRef = useRef(false);
  useEffect(() => {
    if (didInitialSelectRef.current || !dataLoaded) return;
    if (selectedDocId) { didInitialSelectRef.current = true; return; }  // honor a navigated doc
    if (filteredDocs.length === 0) return;                              // wait for the list to populate
    didInitialSelectRef.current = true;
    setSelectedDocId(filteredDocs[0].document_id);
    docListRef.current?.focus();
  }, [dataLoaded, filteredDocs, selectedDocId]);

  // Download current PDF
  const handleDownloadFile = useCallback(() => {
    if (!pdfBytes || !selectedDoc) return;
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = selectedDoc.filename || 'document.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [pdfBytes, selectedDoc]);

  // Open the document in a new browser tab — PREVIEW, not download. We open our OWN
  // in-app viewer route (/doc-view?doc=<id>) which renders the PDF to <canvas> via
  // pdf.js (PdfCanvasViewer). That always shows inline regardless of Chrome's
  // "Download PDFs instead of opening" setting (which would force a Save-As if we
  // handed Chrome a real .pdf). Each click opens its own tab.
  const handleOpenInNewTab = useCallback(() => {
    if (!selectedDoc) return;
    window.open(`/doc-view?doc=${selectedDoc.document_id}`, '_blank');
  }, [selectedDoc]);

  // --- Per-document quick actions (selected doc) ---
  // ★ Permanent: mark the document so the retention job never auto-deletes it.
  // Accepts an explicit doc (per-row button) or falls back to the selected doc.
  const handleTogglePermanent = useCallback(async (docArg?: DocRow) => {
    const doc = docArg || selectedDoc;
    if (!doc) return;
    const next = Number(doc.is_permanent) === 1 ? 0 : 1;
    // saveRow resolves on success OR offline-queue (network error → outbox); it only
    // throws on a real server rejection (4xx). So apply the optimistic state ONLY after
    // it resolves — a rejection surfaces an error and bails (no false "success").
    try {
      await saveRow('dms_documents', doc.document_id, { is_permanent: next, updated_at: NOW_ISO() }, 'document_id');
    } catch {
      setSuccessMsg(`Couldn't update "${doc.filename}". Please try again.`);
      return;
    }
    setDocuments(prev => prev.map(d => d.document_id === doc.document_id ? { ...d, is_permanent: next } : d));
    setSuccessMsg(next
      ? `"${doc.filename}" marked permanent — won't be auto-deleted.`
      : `"${doc.filename}" is no longer marked permanent.`);
  }, [selectedDoc]);

  // Move: re-file the document into another folder.
  const handleMoveDoc = useCallback(async (folderId: number) => {
    if (!selectedDoc) return;
    try {
      await saveRow('dms_documents', selectedDoc.document_id, { dms_folders_id: folderId, updated_at: NOW_ISO() }, 'document_id');
    } catch {
      setSuccessMsg(`Couldn't move "${selectedDoc.filename}". Please try again.`);
      return;   // keep the move popover open so the user can retry
    }
    setDocuments(prev => prev.map(d => d.document_id === selectedDoc.document_id ? { ...d, dms_folders_id: folderId } : d));
    setShowDocMove(false);
    setDocMoveSearch('');
    const fname = folders.find(f => f.folder_id === folderId)?.folder_name || 'folder';
    setSuccessMsg(`Moved "${selectedDoc.filename}" to "${fname}".`);
  }, [selectedDoc, folders]);

  // Delete: open a confirmation modal first — never delete silently.
  const [deleteDocTarget, setDeleteDocTarget] = useState<DocRow | null>(null);
  const [deletingDoc, setDeletingDoc] = useState(false);
  const handleDeleteDoc = useCallback((docArg?: DocRow) => {
    const doc = docArg || selectedDoc;
    if (!doc) return;
    setDeleteDocTarget(doc);
  }, [selectedDoc]);

  // Confirmed: soft-delete the document (reversible; keeps the row).
  const confirmDeleteDoc = useCallback(async () => {
    const doc = deleteDocTarget;
    if (!doc) return;
    setDeletingDoc(true);
    try {
      await saveRow('dms_documents', doc.document_id, { is_deleted: 1, updated_at: NOW_ISO() }, 'document_id');
    } catch {
      setDeletingDoc(false);
      setSuccessMsg(`Couldn't delete "${doc.filename}". Please try again.`);
      return;
    }
    const fname = doc.filename;
    // If the deleted doc was the one open in the viewer, advance to the next doc
    // in the list (or the previous if it was the last) so the viewer + Filing form
    // reload from a fresh document instead of keeping the deleted doc's data.
    const wasSelected = selectedDocId === doc.document_id;
    let nextId: number | null = null;
    if (wasSelected) {
      const idx = filteredDocs.findIndex(d => d.document_id === doc.document_id);
      if (idx !== -1) {
        const after = filteredDocs.slice(idx + 1).find(d => d.document_id !== doc.document_id);
        const before = [...filteredDocs.slice(0, idx)].reverse().find(d => d.document_id !== doc.document_id);
        nextId = (after || before)?.document_id ?? null;
      }
    }
    setDocuments(prev => prev.map(d => d.document_id === doc.document_id ? { ...d, is_deleted: 1 } : d));
    if (wasSelected) {
      setFormDirty(false);          // discard the deleted doc's form state before re-selecting
      setSelectedDocId(nextId);     // null → forms blank (handled by the pre-fill effect)
    }
    setSuccessMsg(`"${fname}" deleted.`);
    setDeletingDoc(false);
    setDeleteDocTarget(null);
  }, [deleteDocTarget, selectedDocId, filteredDocs]);

  // Download all files in a folder as a zip
  const [downloadingFolderId, setDownloadingFolderId] = useState<number | null>(null);
  const handleDownloadAllFiles = useCallback(async (folderId: number, folderName: string) => {
    setFolderMenuOpenId(null);
    const folderDocs = documents.filter(
      // eslint-disable-next-line eqeqeq
      d => d.dms_folders_id === folderId && d.is_deleted != 1 && d.is_active != 0
    );
    if (folderDocs.length === 0) {
      setSuccessMsg('No documents in this folder to download.');
      setTimeout(() => setSuccessMsg(''), 3000);
      return;
    }
    setDownloadingFolderId(folderId);
    try {
      const zip = new JSZip();
      let allDocs: Record<string, any>[];
      try {
        allDocs = await loadTable('dms_documents');
      } catch {
        allDocs = getTableData('dms_documents');
      }
      let added = 0;
      for (const doc of folderDocs) {
        const fullDoc = allDocs.find((d: any) => d.document_id === doc.document_id);
        // Resolve bytes via the blob store (file_path) with an inline-base64 fallback;
        // on live the bytes live on the server volume, not in the row.
        const docBase64 = await resolveDocBase64(fullDoc);
        if (!docBase64) continue;
        try {
          const bytes = await decompressBase64ToBytes(docBase64);
          zip.file(doc.filename || `document_${doc.document_id}.pdf`, bytes);
          added++;
        } catch { /* skip corrupt files */ }
      }
      if (added === 0) {
        setSuccessMsg('No downloadable files found in this folder.');
        setTimeout(() => setSuccessMsg(''), 3000);
        return;
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${folderName.replace(/[^a-zA-Z0-9_-]/g, '_')}_files.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccessMsg(`${added} file(s) zipped and downloading.`);
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch {
      setSuccessMsg('Could not create zip file. Please try again.');
      setTimeout(() => setSuccessMsg(''), 4000);
    } finally {
      setDownloadingFolderId(null);
    }
  }, [documents]);

  // =============================================
  // Filing form — folder helpers
  // =============================================
  const ownerFolders = useMemo(() => {
    return folders.filter(f =>
      // eslint-disable-next-line eqeqeq
      f.is_deleted != 1 && f.is_active == 1 &&
      // eslint-disable-next-line eqeqeq
      f.account_owner_id == accountOwnerId &&
      // Client's Accounts → only this client's folders (dms_client_folders.client_id)
      // eslint-disable-next-line eqeqeq
      (viewMode !== 'clients' || selectedClientId == null || (f as any).client_id == selectedClientId)
    );
  }, [folders, accountOwnerId, viewMode, selectedClientId]);

  // Selectable filing folders = scoped, non-umbrella folders (umbrellas can't hold docs).
  const selectableFolders = useMemo(
    () => ownerFolders.filter(f => Number(f.umbrella_folder) !== 1),
    [ownerFolders],
  );

  // A non-umbrella folder always has an owner. Filing a doc into it should set the
  // document's owner to match the folder's primary owner → returns the "type:id" key.
  const folderOwnerKeyFor = useCallback((folderId: number): string => {
    const fOwners = folderOwners.filter(o => o.folder_id === folderId && o.is_deleted !== 1);
    const primary = fOwners.find(o => Number(o.is_primary) === 1) || fOwners[0];
    return primary ? `${primary.owner_type}:${primary.owner_id}` : '';
  }, [folderOwners]);

  // Primary-owner nickname for a folder (users.nickname / business acronym) — shown
  // in the folder picker in place of the internal folder_path serial.
  const folderNickFor = useCallback((folderId: number): string => {
    const key = folderOwnerKeyFor(folderId);
    if (!key) return '';
    const [t, id] = key.split(':');
    return getOwnerNickname(t, Number(id), usersData, businessesData);
  }, [folderOwnerKeyFor, usersData, businessesData]);

  // Owner entities reduced to the names worth matching a document against
  // (display name + nickname/business acronym). Feeds the conservative auto-matcher.
  const ownerMatchCandidates = useMemo<OwnerMatchCandidate[]>(() =>
    ownerEntities.map(e => ({
      key: `${e.owner_type}:${e.owner_id}`,
      names: [e.display_name, getOwnerNickname(e.owner_type, e.owner_id, usersData, businessesData)].filter(Boolean),
    })),
  [ownerEntities, usersData, businessesData]);

  // Selectable (scoped, non-umbrella) folders reduced to name + primary-owner key,
  // so a matched owner can disambiguate same-named folders across owners. Mirrors
  // the picker's source (ownerFolders sans umbrella) — a suggestion is always pickable.
  const folderMatchCandidates = useMemo<FolderMatchCandidate[]>(() =>
    ownerFolders
      .filter(f => Number(f.umbrella_folder) !== 1)
      .map(f => ({ id: f.folder_id, name: f.folder_name, ownerKey: folderOwnerKeyFor(f.folder_id) })),
  [ownerFolders, folderOwnerKeyFor]);

  // Toggle an owner in the multi-select "Owner Of Document" field.
  const toggleFilingOwner = useCallback((key: string) => {
    setFilingOwnerKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setFormDirty(true);
  }, []);

  // Toggle a folder in the multi-select "Select folder" field.
  const toggleFilingFolder = useCallback((id: number) => {
    setFilingFolderIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    setFormDirty(true);
  }, []);

  // Owner options for the multi-select, with the already-selected pinned to the top.
  // Name only — no "(Owner)/(Family)/(Business)" badge in parentheses.
  const ownerSelectOptions = useMemo(() => {
    const all = ownerEntities.map(e => ({
      value: `${e.owner_type}:${e.owner_id}`,
      label: e.display_name,
    }));
    const sel = (v: string) => filingOwnerKeys.includes(v);
    return [...all.filter(o => sel(o.value)), ...all.filter(o => !sel(o.value))];
  }, [ownerEntities, filingOwnerKeys]);

  // Folder options for the multi-select, with the already-selected pinned to the top.
  const folderSelectOptions = useMemo(() => {
    const nick = (id: number) => (folderNickFor(id) ? ` · ${folderNickFor(id)}` : '');
    const all = selectableFolders.map(f => ({
      value: String(f.folder_id),
      label: `${f.folder_name}${nick(f.folder_id)}`,
    }));
    const sel = (v: string) => filingFolderIds.includes(Number(v));
    return [...all.filter(o => sel(o.value)), ...all.filter(o => !sel(o.value))];
  }, [selectableFolders, folderNickFor, filingFolderIds]);

  // =============================================
  // Filing Submit
  // =============================================
  const handleFilingSubmit = useCallback(async () => {
    if (!selectedDoc) return;
    if (filingOwnerKeys.length === 0) return;   // at least one owner
    if (filingFolderIds.length === 0) return;   // at least one folder

    // Primary owner / folder live on dms_documents; the rest in the link tables.
    const primaryOwnerKey = filingOwnerKeys[0];
    const primaryFolderId = filingFolderIds[0];
    const [ownerType, ownerIdStr] = primaryOwnerKey.split(':');
    const ownerId = Number(ownerIdStr);
    const now = NOW_ISO();

    // Best-effort: register/refresh the party in the sitewide directory and learn its
    // date format. Non-fatal — filing must succeed even if the API is unavailable
    // (localStorage/offline mode). Public business facts only; never PII.
    let busiId: number | null = filingGlobalBusiId;
    if (filingPartyName && filingPartyName.trim()) {
      try {
        const facts = filingBusinessFactsRef.current || {};
        const resp = await apiClient.post<{ busi_id: number; country_id: number }>('/directory/upsert', {
          canonical_name: filingPartyName.trim(),
          country_id: 0,                          // unknown jurisdiction → DEFAULT partition (future: resolve owner/party country)
          address_1: facts.address_1 || undefined,
          website: facts.website || undefined,
          general_phone: facts.general_phone || undefined,
          general_email: facts.general_email || undefined,
          category_code: facts.category_code || undefined,
          date_format_id: filingDateFormatId || undefined,
          sample_document_id: selectedDoc.document_id,
          source_document_id: selectedDoc.document_id,
        });
        if (resp.data && resp.data.busi_id) busiId = Number(resp.data.busi_id);
        // Learn the party's date format from this filing (so the next ambiguous date resolves).
        if (busiId && filingDateFormatId) {
          await apiClient.post('/directory/date-format', {
            busi_id: busiId, country_id: 0, date_format_id: filingDateFormatId,
            sample_document_id: selectedDoc.document_id, sample_raw_date: filingRawDate || undefined,
            evidence_kind: 'user_confirmed',
          });
        }
      } catch { /* directory is best-effort */ }
    }

    const updates = {
      status: 'Filed',
      owner_type: ownerType,
      owner_id: ownerId,
      docu_type_id: filingDocuTypeId ? Number(filingDocuTypeId) : null,
      party_name: filingPartyName || null,
      invoice_ref: filingInvoiceRef || null,
      date_of_document: filingDate || null,
      payment_method: filingPaymentMethod || null,
      payment_method_id: filingPaymentMethodId ? Number(filingPaymentMethodId) : null,
      tax_type_id: filingTaxTypeId ? Number(filingTaxTypeId) : null,
      tax_rate_id: filingTaxRateId ? Number(filingTaxRateId) : null,
      tax_amount: filingTaxAmount ? Number(filingTaxAmount) : null,
      date_format_id: filingDateFormatId,
      raw_date_string: filingRawDate || null,
      global_busi_id: busiId,
      total_amount: filingAmount ? Number(filingAmount) : null,
      currency: filingCurrency || null,
      purpose: filingPurpose || null,
      filename: filingFilename.trim() || selectedDoc.filename,
      dms_folders_id: primaryFolderId,
      updated_at: now,
    };

    // Persist the filing. A real server error (not a network/offline blip, which
    // saveRow queues+applies locally) means the DB did NOT accept it — surface it and
    // bail instead of faking success with an optimistic update that reverts on reload.
    try {
      await saveRow('dms_documents', selectedDoc.document_id, updates, 'document_id');
    } catch (err: any) {
      setSuccessMsg('');
      setErrorMsg(`Could not file "${selectedDoc.filename}": ${err?.message || 'server rejected the change'}. Not filed.`);
      return;
    }
    setErrorMsg('');

    // Update local state
    setDocuments(prev => prev.map(d =>
      d.document_id === selectedDoc.document_id ? { ...d, ...updates } : d
    ));
    justProcessedRef.current = true;   // drives the To File → Rejected hand-off

    // Reconcile multi-owner rows (dms_document_owners): one row per selected owner so
    // the same stored document surfaces under every owner. folder_id is left null —
    // folders are now tracked independently in dms_document_folders. Removed owners
    // are soft-deleted.
    const desiredOwners = new Map<string, number>();   // key → is_primary
    filingOwnerKeys.forEach((key, idx) => { if (key && !desiredOwners.has(key)) desiredOwners.set(key, idx === 0 ? 1 : 0); });
    const existingOwnerRows = docOwners.filter(r => r.document_id === selectedDoc.document_id);
    const nextDocOwners = [...docOwners];
    try {
      for (const [key, primary] of Array.from(desiredOwners.entries())) {
        const [ot, oidStr] = key.split(':');
        const oid = Number(oidStr);
        const existing = existingOwnerRows.find(r => `${r.owner_type}:${r.owner_id}` === key);
        if (existing) {
          const patch = { is_primary: primary, is_active: 1, is_deleted: 0, updated_at: now };
          await saveRow('dms_document_owners', existing.doc_owner_id, patch, 'doc_owner_id');
          const i = nextDocOwners.findIndex(x => x.doc_owner_id === existing.doc_owner_id);
          if (i >= 0) nextDocOwners[i] = { ...nextDocOwners[i], ...patch };
        } else {
          const created = await addRow('dms_document_owners', {
            document_id: selectedDoc.document_id,
            owner_type: ot, owner_id: oid,
            is_primary: primary, access_level: 'manage',
            folder_id: null, is_active: 1, is_deleted: 0,
            added_by: currentUser ? currentUser.user_id : null,
            created_at: now, updated_at: now,
          }, 'doc_owner_id');
          nextDocOwners.push(created as DocOwnerRow);
        }
      }
      for (const r of existingOwnerRows) {
        const key = `${r.owner_type}:${r.owner_id}`;
        if (desiredOwners.has(key) || Number(r.is_deleted) === 1) continue;
        const patch = { is_active: 0, is_deleted: 1, updated_at: now };
        await saveRow('dms_document_owners', r.doc_owner_id, patch, 'doc_owner_id');
        const i = nextDocOwners.findIndex(x => x.doc_owner_id === r.doc_owner_id);
        if (i >= 0) nextDocOwners[i] = { ...nextDocOwners[i], ...patch };
      }
      setDocOwners(nextDocOwners);
    } catch {
      // best-effort — the primary owner is already saved on dms_documents
    }

    // Reconcile multi-folder rows (dms_document_folders): one row per selected folder
    // so the doc surfaces inside every folder. Removed folders are soft-deleted.
    const desiredFolders = new Map<number, number>();   // folder_id → is_primary
    filingFolderIds.forEach((fid, idx) => { if (fid != null && !desiredFolders.has(fid)) desiredFolders.set(fid, idx === 0 ? 1 : 0); });
    const existingFolderRows = docFolders.filter(r => r.document_id === selectedDoc.document_id);
    const nextDocFolders = [...docFolders];
    try {
      for (const [fid, primary] of Array.from(desiredFolders.entries())) {
        const existing = existingFolderRows.find(r => Number(r.folder_id) === fid);
        if (existing) {
          const patch = { is_primary: primary, is_active: 1, is_deleted: 0, updated_at: now };
          await saveRow('dms_document_folders', existing.doc_folder_id, patch, 'doc_folder_id');
          const i = nextDocFolders.findIndex(x => x.doc_folder_id === existing.doc_folder_id);
          if (i >= 0) nextDocFolders[i] = { ...nextDocFolders[i], ...patch };
        } else {
          const created = await addRow('dms_document_folders', {
            document_id: selectedDoc.document_id,
            folder_id: fid, is_primary: primary, is_active: 1, is_deleted: 0,
            added_by: currentUser ? currentUser.user_id : null,
            created_at: now, updated_at: now,
          }, 'doc_folder_id');
          nextDocFolders.push(created as DocFolderRow);
        }
      }
      for (const r of existingFolderRows) {
        if (desiredFolders.has(Number(r.folder_id)) || Number(r.is_deleted) === 1) continue;
        const patch = { is_active: 0, is_deleted: 1, updated_at: now };
        await saveRow('dms_document_folders', r.doc_folder_id, patch, 'doc_folder_id');
        const i = nextDocFolders.findIndex(x => x.doc_folder_id === r.doc_folder_id);
        if (i >= 0) nextDocFolders[i] = { ...nextDocFolders[i], ...patch };
      }
      setDocFolders(nextDocFolders);
    } catch {
      // best-effort — the primary folder is already saved on dms_documents
    }

    setFormDirty(false);
    setSuccessMsg(`"${selectedDoc.filename}" filed successfully!`);

    if (currentUser && currentUser.user_id !== accountOwnerId) {
      addNotification({
        account_owner_id: accountOwnerId,
        type: 'receipt_filed',
        title: 'Receipt Filed',
        message: `${currentUser.first_name} filed "${selectedDoc.filename}"`,
        actor_user_id: currentUser.user_id,
        actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
      });
    }

    // Auto-advance to next doc
    const nextIdx = currentIndex + 1;
    if (nextIdx < filteredDocs.length) {
      setSelectedDocId(filteredDocs[nextIdx].document_id);
    }
  }, [selectedDoc, filingOwnerKeys, filingDocuTypeId, filingPartyName, filingInvoiceRef,
      filingDate, filingPaymentMethod, filingPaymentMethodId, filingTaxTypeId, filingTaxRateId,
      filingTaxAmount, filingDateFormatId, filingRawDate, filingGlobalBusiId,
      filingAmount, filingCurrency, filingPurpose,
      filingFolderIds, docOwners, docFolders, currentUser, accountOwnerId, currentIndex, filteredDocs]);

  // =============================================
  // Reject
  // =============================================
  const handleReject = useCallback(async () => {
    if (!selectedDoc) return;
    const now = NOW_ISO();
    const updates = { status: 'Rejected', updated_at: now };

    try {
      await saveRow('dms_documents', selectedDoc.document_id, updates, 'document_id');
    } catch {
      // saveRow handles localStorage fallback internally
    }

    setDocuments(prev => prev.map(d =>
      d.document_id === selectedDoc.document_id ? { ...d, ...updates } : d
    ));
    justProcessedRef.current = true;
    setSuccessMsg(`"${selectedDoc.filename}" rejected.`);

    const nextIdx = currentIndex + 1;
    if (nextIdx < filteredDocs.length) {
      setSelectedDocId(filteredDocs[nextIdx].document_id);
    }
  }, [selectedDoc, currentIndex, filteredDocs]);

  // =============================================
  // Bookkeeping Publish
  // =============================================
  const handleBkPublish = useCallback(async () => {
    if (!selectedDoc) return;
    if (!bkOwnerKey) return;

    const [ownerType, ownerIdStr] = bkOwnerKey.split(':');
    const ownerId = Number(ownerIdStr);
    const now = NOW_ISO();

    const updates = {
      status: 'BK Done',
      owner_type: ownerType,
      owner_id: ownerId,
      docu_type_id: bkDocuTypeId ? Number(bkDocuTypeId) : selectedDoc.docu_type_id,
      party_name: bkPartyName || selectedDoc.party_name,
      invoice_ref: bkInvoiceRef || selectedDoc.invoice_ref,
      date_of_document: bkDate || selectedDoc.date_of_document,
      due_date: bkDueDate || null,
      total_amount: bkAmount ? Number(bkAmount) : selectedDoc.total_amount,
      currency: bkCurrency || selectedDoc.currency,
      tax_rate: bkTaxRate ? Number(bkTaxRate) : null,
      updated_at: now,
    };

    try {
      await saveRow('dms_documents', selectedDoc.document_id, updates, 'document_id');
    } catch {
      // saveRow handles localStorage fallback internally
    }

    setDocuments(prev => prev.map(d =>
      d.document_id === selectedDoc.document_id ? { ...d, ...updates } : d
    ));
    justProcessedRef.current = true;
    setSuccessMsg(`"${selectedDoc.filename}" bookkeeping published!`);

    if (currentUser && currentUser.user_id !== accountOwnerId) {
      addNotification({
        account_owner_id: accountOwnerId,
        type: 'bk_published',
        title: 'Bookkeeping Published',
        message: `${currentUser.first_name} published bookkeeping for "${selectedDoc.filename}"`,
        actor_user_id: currentUser.user_id,
        actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
      });
    }

    const nextIdx = currentIndex + 1;
    if (nextIdx < filteredDocs.length) {
      setSelectedDocId(filteredDocs[nextIdx].document_id);
    }
  }, [selectedDoc, bkOwnerKey, bkDocuTypeId, bkPartyName, bkInvoiceRef, bkDate,
      bkDueDate, bkAmount, bkCurrency, bkTaxRate, currentUser, accountOwnerId,
      currentIndex, filteredDocs]);

  // =============================================
  // Bank transactions (line-items from a Convert-to-CSV statement)
  // =============================================
  // The imported acctg_bank_txn rows linked to the selected statement document.
  const docBankTxns = useMemo(
    () => selectedDoc
      ? bankTxns.filter(t => t.document_id === selectedDoc.document_id && t.is_deleted !== 1)
      : [],
    [bankTxns, selectedDoc]
  );

  // doc_id → number of linked bank txns (for the doc-list badge).
  const txnCountByDoc = useMemo(() => {
    const m = new Map<number, number>();
    bankTxns.forEach(t => {
      if (t.is_deleted === 1 || t.document_id == null) return;
      m.set(t.document_id, (m.get(t.document_id) || 0) + 1);
    });
    return m;
  }, [bankTxns]);

  // GL accounts to map a txn to — scoped to the document's owner (falls back to
  // all of the account owner's detail accounts if the owner has none yet).
  const coaOptions = useMemo<DropdownOption[]>(() => {
    if (!selectedDoc) return [];
    const accts = coaAccounts.filter(a =>
      a.is_deleted !== 1 && a.is_active !== 0 && Number(a.is_header) !== 1 &&
      // eslint-disable-next-line eqeqeq
      a.account_owner_id == accountOwnerId
    );
    const owned = selectedDoc.owner_type && selectedDoc.owner_id != null
      // eslint-disable-next-line eqeqeq
      ? accts.filter(a => a.owner_type === selectedDoc.owner_type && a.owner_id == selectedDoc.owner_id)
      : [];
    const list = owned.length ? owned : accts;
    return list
      .slice()
      .sort((a, b) => (a.account_number || '').localeCompare(b.account_number || ''))
      .map(a => ({
        value: String(a.coa_id),
        label: `${a.account_number ? a.account_number + ' ' : ''}${a.account_name}`,
      }));
  }, [coaAccounts, selectedDoc, accountOwnerId]);

  // Reset the editable drafts whenever the selected doc's txns change.
  useEffect(() => {
    setTxnDrafts(docBankTxns.map(t => ({
      txn_id: t.txn_id,
      coa_id: t.coa_id == null ? null : Number(t.coa_id),
      category: t.category || '',
      status: t.status || 'imported',
    })));
  }, [docBankTxns]);

  const updateTxnDraft = useCallback((txnId: number, patch: Partial<TxnDraft>) => {
    setTxnDrafts(prev => prev.map(d => d.txn_id === txnId ? { ...d, ...patch } : d));
    setFormDirty(true);
  }, []);

  const markAllTxnsReviewed = useCallback(() => {
    setTxnDrafts(prev => prev.map(d => ({ ...d, status: 'reviewed' })));
    setFormDirty(true);
  }, []);

  const handleSaveBankTxns = useCallback(async (): Promise<boolean> => {
    const now = NOW_ISO();
    setTxnBusy(true);
    let saved = 0;
    for (const draft of txnDrafts) {
      const orig = bankTxns.find(t => t.txn_id === draft.txn_id);
      if (!orig) continue;
      const changed =
        (orig.coa_id ?? null) !== (draft.coa_id ?? null) ||
        (orig.category ?? '') !== (draft.category ?? '') ||
        (orig.status ?? '') !== (draft.status ?? '');
      if (!changed) continue;
      const updates = {
        coa_id: draft.coa_id,
        category: draft.category || null,
        status: draft.status,
        updated_at: now,
      };
      try {
        await saveRow('acctg_bank_txn', draft.txn_id, updates, 'txn_id');
        saved++;
      } catch { /* saveRow handles localStorage fallback internally */ }
    }
    // Reflect drafts into local state
    setBankTxns(prev => prev.map(t => {
      const d = txnDrafts.find(x => x.txn_id === t.txn_id);
      return d ? { ...t, coa_id: d.coa_id, category: d.category, status: d.status } : t;
    }));
    setFormDirty(false);
    setTxnBusy(false);
    if (saved > 0) {
      setSuccessMsg(`Saved ${saved} transaction assignment${saved === 1 ? '' : 's'}.`);
      if (currentUser && currentUser.user_id !== accountOwnerId) {
        addNotification({
          account_owner_id: accountOwnerId,
          type: 'bank_txn_updated',
          title: 'Bank Transactions Updated',
          message: `${currentUser.first_name} updated ${saved} bank transaction${saved === 1 ? '' : 's'}`,
          actor_user_id: currentUser.user_id,
          actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
        });
      }
    } else {
      setSuccessMsg('No changes to save.');
    }
    return true;
  }, [txnDrafts, bankTxns, currentUser, accountOwnerId]);

  // txn_id of every bank txn already posted to the GL (idempotency guard for "Post to GL").
  const postedTxnIds = useMemo(() => {
    const s = new Set<number>();
    glJournals.forEach(j => {
      if (j.is_deleted !== 1 && j.status === 'posted' && j.source === 'bank_txn' && j.source_txn_id != null) {
        s.add(Number(j.source_txn_id));
      }
    });
    return s;
  }, [glJournals]);

  // Post mapped bank transactions to the GL: each becomes a balanced journal entry
  // (mapped account vs the chosen bank/cash account, per direction). Idempotent —
  // already-posted txns are skipped; unmapped/rejected are skipped + reported.
  const handlePostToGl = useCallback(async () => {
    if (!bankAccountCoaId) { setSuccessMsg('Pick the bank/cash account for this statement first.'); return; }
    const bankCoa = Number(bankAccountCoaId);
    const eligible = docBankTxns.filter(t => t.is_deleted !== 1 && t.status !== 'rejected' && !postedTxnIds.has(t.txn_id));
    const candidates = eligible.filter(t => t.coa_id != null);
    const skippedNoAcct = eligible.length - candidates.length;
    if (candidates.length === 0) {
      setSuccessMsg(skippedNoAcct > 0
        ? `Nothing to post — ${skippedNoAcct} transaction${skippedNoAcct === 1 ? '' : 's'} still need a GL account.`
        : 'Nothing new to post.');
      return;
    }
    setTxnBusy(true);
    let posted = 0;
    const newJournals: Record<string, any>[] = [];
    const balanceById = new Map<number, number>();   // accumulate balances across this run
    try {
      for (const t of candidates) {
        // Reflect already-applied balance changes (e.g. the bank account hit repeatedly).
        const coaSnapshot = coaAccounts.map(a => balanceById.has(Number(a.coa_id))
          ? { ...a, current_balance: balanceById.get(Number(a.coa_id)) } : a);
        const lines = bankTxnToLines(
          { direction: t.direction, amount: t.amount ?? 0, coa_id: Number(t.coa_id), description: t.description },
          bankCoa,
        );
        const result = await postJournalEntry({
          accountOwnerId,
          ownerType: t.owner_type || 'account_owner',
          ownerId: Number(t.owner_id),
          date: (t.txn_date || '').slice(0, 10) || NOW_ISO().slice(0, 10),
          memo: t.description || '',
          source: 'bank_txn',
          sourceTxnId: t.txn_id,
          documentId: t.document_id,
          currency: t.currency || 'CAD',
          createdBy: currentUser ? currentUser.user_id : null,
          lines,
          coaAccounts: coaSnapshot as any,
        });
        result.balanceUpdates.forEach(u => balanceById.set(u.coa_id, u.current_balance));
        newJournals.push(result.journal);
        await saveRow('acctg_bank_txn', t.txn_id, { status: 'posted', updated_at: NOW_ISO() }, 'txn_id');
        posted++;
      }
      setGlJournals(prev => [...newJournals, ...prev]);
      setCoaAccounts(prev => prev.map(a => balanceById.has(Number(a.coa_id))
        ? { ...a, current_balance: balanceById.get(Number(a.coa_id))! } : a));
      const postedSet = new Set(candidates.map(c => c.txn_id));
      setBankTxns(prev => prev.map(t => postedSet.has(t.txn_id) ? { ...t, status: 'posted' } : t));
      const parts = [`Posted ${posted} ${posted === 1 ? 'entry' : 'entries'} to the GL`];
      if (skippedNoAcct > 0) parts.push(`${skippedNoAcct} skipped (no account)`);
      setSuccessMsg(parts.join(' · ') + '.');
      if (currentUser && currentUser.user_id !== accountOwnerId) {
        addNotification({
          account_owner_id: accountOwnerId, type: 'gl_posted', title: 'Bank Transactions Posted',
          message: `${currentUser.first_name} posted ${posted} transaction${posted === 1 ? '' : 's'} to the GL`,
          actor_user_id: currentUser.user_id, actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
        });
      }
    } catch (e: any) {
      setSuccessMsg(e?.message ? `Posting stopped: ${e.message}` : 'Could not post to the GL. Please try again.');
    } finally {
      setTxnBusy(false);
    }
  }, [bankAccountCoaId, docBankTxns, postedTxnIds, coaAccounts, accountOwnerId, currentUser]);

  // "Save & continue" for the active right-panel form (Filing / Bookkeeping / Bank Txns) — modal.
  // Skip while the Manage Folders popup is open (it registers its own save handler);
  // re-registers when the popup closes (showManageFolders in deps).
  useEffect(() => {
    if (showManageFolders) return;
    // The Make Journal Entries tab is a self-contained child that registers its OWN
    // save handler — don't fight it (mirror the Manage Folders skip above).
    if (rightTab === 'makejournal') return;
    const saver = rightTab === 'banktxn'
      ? handleSaveBankTxns
      : submitSaver(rightTab === 'bookkeeping' ? handleBkPublish : handleFilingSubmit);
    registerSaveHandler(saver);
    return () => registerSaveHandler(null);
  }, [rightTab, handleFilingSubmit, handleBkPublish, handleSaveBankTxns, showManageFolders]);

  // =============================================
  // Bookkeeping computed values
  // =============================================
  const bkComputed = useMemo(() => {
    const amount = Number(bkAmount) || 0;
    const rate = Number(bkTaxRate) || 0;
    const tax = rate > 0 ? (amount * rate) / (100 + rate) : 0;
    const subtotal = amount - tax;
    return { subtotal, tax, total: amount };
  }, [bkAmount, bkTaxRate]);

  // =============================================
  // Record Room — folder tree
  // =============================================
  const visibleFolderIds = useMemo(() => {
    const term = folderSearchTerm.trim().toLowerCase();
    const hasOwner = scopedOwnerKeys.length > 0;
    // No search and no owner picked → show the whole tree.
    if (!term && !hasOwner) return null;

    // Folder ids owned by ANY selected owner entity (incl. joint ownership).
    let ownedIds: Set<number> | null = null;
    if (hasOwner) {
      ownedIds = new Set<number>();
      scopedOwnerKeys.forEach(key => {
        const [oType, oIdStr] = key.split(':');
        const oId = Number(oIdStr);
        folderOwners
          // eslint-disable-next-line eqeqeq
          .filter(o => o.is_deleted !== 1 && o.owner_type === oType && o.owner_id == oId)
          .forEach(o => ownedIds!.add(Number(o.folder_id)));
      });
    }

    // A folder is a "match" when it passes BOTH active filters (owner + search).
    const matched = ownerFolders.filter(f => {
      if (ownedIds && !ownedIds.has(f.folder_id)) return false;
      if (term && !f.folder_name?.toLowerCase().includes(term)) return false;
      return true;
    });

    // Include each match plus its ancestors so the tree stays navigable.
    const ids = new Set<number>();
    matched.forEach(f => {
      ids.add(f.folder_id);
      let current = f;
      while (current.parent_folder_id != null) {
        ids.add(current.parent_folder_id);
        const parent = ownerFolders.find(p => p.folder_id === current.parent_folder_id);
        if (!parent) break;
        current = parent;
      }
    });
    return ids;
  }, [ownerFolders, folderOwners, folderSearchTerm, scopedOwnerKeys]);

  const rootFolders = useMemo(() => {
    return ownerFolders
      .filter(f => f.parent_folder_id == null && (visibleFolderIds === null || visibleFolderIds.has(f.folder_id)))
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [ownerFolders, visibleFolderIds]);

  const getChildFolders = useCallback((parentId: number) => {
    return ownerFolders
      .filter(f => f.parent_folder_id === parentId && (visibleFolderIds === null || visibleFolderIds.has(f.folder_id)))
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [ownerFolders, visibleFolderIds]);

  const getDocsInFolder = useCallback((folderId: number) => {
    return documents.filter(d =>
      // eslint-disable-next-line eqeqeq
      d.is_deleted != 1 && d.is_active == 1 &&
      d.dms_folders_id === folderId &&
      // eslint-disable-next-line eqeqeq
      d.account_owner_id == accountOwnerId
    );
  }, [documents, accountOwnerId]);

  const toggleFolder = useCallback((folderId: number) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId); else next.add(folderId);
      return next;
    });
  }, []);

  // Drilled in from the Reports dashboard → highlight the target folder and expand
  // its ancestors so it's visible. Runs once the folder tree has loaded.
  useEffect(() => {
    if (initialSelectedFolderId == null || folders.length === 0) return;
    setShowRecordRoom(true);
    setSelectedRecordFolderId(initialSelectedFolderId);
    setExpandedFolders(prev => {
      const next = new Set(prev);
      let cur = folders.find(f => f.folder_id === initialSelectedFolderId);
      let guard = 0;
      while (cur && cur.parent_folder_id != null && guard < 20) {
        next.add(Number(cur.parent_folder_id));
        cur = folders.find(f => f.folder_id === cur!.parent_folder_id);
        guard++;
      }
      return next;
    });
  }, [initialSelectedFolderId, folders]);

  // =============================================
  // Render helpers
  // =============================================
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Review': return <span className="pr-doc-status-icon review">?</span>;
      case 'Filed': return <span className="pr-doc-status-icon filed"><FiCheck /></span>;
      case 'Rejected': return <span className="pr-doc-status-icon rejected"><FiX /></span>;
      case 'BK Done': return <span className="pr-doc-status-icon bk-done"><FiCheck /></span>;
      default: return <span className="pr-doc-status-icon review">?</span>;
    }
  };

  const getDocOwnerLabel = (doc: DocRow): string => {
    if (doc.owner_type && doc.owner_id) {
      const name = getOwnerName(doc.owner_type, doc.owner_id, usersData, businessesData);
      if (name) {
        const ent = ownerEntities.find(e => e.owner_type === doc.owner_type && e.owner_id === doc.owner_id);
        return `${name}${ent ? ` (${ent.badge})` : ''}`;
      }
    }
    // Fallback: look up user_id
    // eslint-disable-next-line eqeqeq
    const u = usersData.find((u: any) => u.user_id == doc.user_id);
    if (u) {
      const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
      return name || u.email || '';
    }
    return '';
  };

  // =============================================
  // Render: Folder tree node (recursive)
  // =============================================
  const handleFolderFilter = useCallback((folderId: number) => {
    setSelectedRecordFolderId(prev => prev === folderId ? null : folderId);
    setSelectedDocId(null);
  }, []);

  const renderFolderNode = (folder: FolderRow): React.ReactNode => {
    const children = getChildFolders(folder.folder_id);
    const docsInFolder = getDocsInFolder(folder.folder_id);
    const isExpanded = expandedFolders.has(folder.folder_id) || visibleFolderIds !== null;
    const hasChildren = children.length > 0 || docsInFolder.length > 0;
    const isActiveFilter = selectedRecordFolderId === folder.folder_id;
    const emoji = FOLDER_EMOJI[folder.folder_icon || 'other'] || '📁';

    // Left marker: umbrella folders → umbrella icon; owned folders → owner's
    // nickname badge (site-orange); otherwise the folder type emoji.
    const isUmbrella = Number(folder.umbrella_folder) === 1;
    const fOwners = folderOwners.filter(o => o.folder_id === folder.folder_id && o.is_deleted !== 1);
    const primaryOwner = fOwners.find(o => Number(o.is_primary) === 1) || fOwners[0];
    const ownerNick = primaryOwner ? getOwnerNickname(primaryOwner.owner_type, primaryOwner.owner_id, usersData, businessesData) : '';
    const ownerName = primaryOwner ? getOwnerName(primaryOwner.owner_type, primaryOwner.owner_id, usersData, businessesData) : '';
    const extraOwners = fOwners.length > 1 ? fOwners.length - 1 : 0;

    return (
      <div key={folder.folder_id} className="pr-folder-node">
        <div
          className={`pr-folder-row ${isActiveFilter ? 'pr-folder-active' : ''} ${dropTargetId === folder.folder_id ? 'pr-folder-droptarget' : ''} ${draggedFolderId === folder.folder_id ? 'pr-folder-dragging' : ''}`}
          draggable={renamingFolderId !== folder.folder_id && !folderMoving}
          onDragStart={(e) => handleFolderDragStart(e, folder.folder_id)}
          onDragOver={(e) => handleFolderDragOver(e, folder.folder_id)}
          onDragLeave={() => setDropTargetId(prev => (prev === folder.folder_id ? null : prev))}
          onDrop={(e) => handleFolderDrop(e, folder.folder_id)}
          onDragEnd={handleFolderDragEnd}
          title={draggedFolderId != null ? 'Drop here to move into this folder' : undefined}
        >
          <span
            className={`pr-folder-expand ${isExpanded ? 'expanded' : ''}`}
            onClick={() => hasChildren && toggleFolder(folder.folder_id)}
          >
            {hasChildren ? <FiChevronRight /> : ''}
          </span>
          {isUmbrella ? (
            <span className="pr-folder-icon pr-folder-umbrella" title="Umbrella folder (groups sub-folders)">&#9748;</span>
          ) : ownerNick ? (
            <span
              className="pr-folder-nick"
              title={ownerName + (extraOwners ? ` +${extraOwners} more owner${extraOwners === 1 ? '' : 's'}` : '')}
            >
              {ownerNick}
            </span>
          ) : (
            <span className="pr-folder-icon">{emoji}</span>
          )}
          {renamingFolderId === folder.folder_id ? (
            <span className="pr-folder-rename">
              <input
                className="pr-folder-rename-input"
                value={renameValue}
                autoFocus
                disabled={folderBusy}
                onChange={e => setRenameValue(e.target.value)}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => {
                  if (e.key === 'Enter') submitRename();
                  else if (e.key === 'Escape') setRenamingFolderId(null);
                }}
              />
              <button className="pr-folder-inline-btn save" onClick={submitRename} disabled={folderBusy} title="Save"><FiCheck size={13} /></button>
              <button className="pr-folder-inline-btn cancel" onClick={() => setRenamingFolderId(null)} title="Cancel"><FiX size={13} /></button>
            </span>
          ) : (
            <span
              className={`pr-folder-name pr-folder-name-clickable ${nameHasLongToken(folder.folder_name) ? 'pr-folder-name-fill' : ''}`}
              onClick={() => handleFolderFilter(folder.folder_id)}
              onDoubleClick={() => { if (hasChildren) toggleFolder(folder.folder_id); }}
              title={hasChildren ? 'Double-click to open / collapse this folder' : isActiveFilter ? 'Click to clear folder filter' : 'Click to filter documents by this folder'}
            >
              {folder.folder_name}
            </span>
          )}
          <span className="pr-folder-menu-wrapper" ref={folderMenuOpenId === folder.folder_id ? folderMenuRef : undefined}>
            <button
              className="pr-folder-menu-btn"
              aria-expanded={folderMenuOpenId === folder.folder_id}
              onClick={(e) => { e.stopPropagation(); setFolderMenuOpenId(prev => prev === folder.folder_id ? null : folder.folder_id); }}
              title="Folder options"
            >
              <FiChevronDown size={16} />
            </button>
            {folderMenuOpenId === folder.folder_id && (
              <div className="pr-folder-context-menu">
                <button className="pr-folder-context-item" onClick={openManageFolders}>Add Folder…</button>
                <button className="pr-folder-context-item" onClick={() => startRename(folder)}>Rename Folder</button>
                <button className="pr-folder-context-item danger" onClick={() => startDeleteFolder(folder.folder_id)}>Delete Folder</button>
                <div className="pr-folder-context-divider" />
                <button
                  className="pr-folder-context-item"
                  onClick={() => handleDownloadAllFiles(folder.folder_id, folder.folder_name)}
                  disabled={downloadingFolderId === folder.folder_id}
                >
                  {downloadingFolderId === folder.folder_id
                    ? <><span className="pr-ctx-spinner" /> Zipping...</>
                    : 'Download All Files'}
                </button>
                <button className="pr-folder-context-item" onClick={() => handleExportCsv(folder)}>Export To CSV</button>
                <button className="pr-folder-context-item" onClick={handlePushToDrive}>Push to Google Drive</button>
              </div>
            )}
          </span>
        </div>
        {confirmDeleteFolderId === folder.folder_id && (
          <div className="pr-folder-inline-confirm">
            <span className="pr-folder-inline-confirm-text">Delete "{folder.folder_name}" and its sub-folders?</span>
            <div className="pr-folder-inline-confirm-actions">
              <button className="pr-confirm-del-btn" onClick={confirmDeleteFolder} disabled={folderBusy}>{folderBusy ? 'Deleting…' : 'Delete'}</button>
              <button className="pr-confirm-cancel-btn" onClick={() => setConfirmDeleteFolderId(null)} disabled={folderBusy}>Cancel</button>
            </div>
          </div>
        )}
        {isExpanded && (
          <div className="pr-folder-children">
            {children.map(child => renderFolderNode(child))}
            {docsInFolder.map(doc => (
              <div
                key={doc.document_id}
                className="pr-folder-doc-item"
                onClick={() => setSelectedDocId(doc.document_id)}
              >
                <FiFileText className="pr-folder-doc-icon" />
                <span className="pr-folder-name">{doc.filename}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // =============================================
  // RENDER
  // =============================================
  if (!accountOwnerId) {
    return (
      <div className="pr-no-doc-msg">
        <FiAlertCircle style={{ fontSize: 32, marginBottom: 8 }} />
        <p>Please log in to process receipts.</p>
      </div>
    );
  }

  return (
    <div className={`pr-container ${foldersOnly ? 'pr-folders-only' : ''}`}>
      {/* ======== Record Room Sidebar ======== */}
      {(showRecordRoom || foldersOnly) && (
        <div className="pr-record-room">
          {/* The "Record Room (Folders)" caption is already on the toggle button
              beside this panel, so it's omitted here to free vertical space. */}
          {/* Owner scope — compact, multi-select; scopes BOTH the folder tree
              and the document list. One-line trigger opens a searchable list so
              the folder tree keeps its vertical space. Account owner picks any
              owner; a regular user is locked to their own folders. */}
          <div className="pr-folder-owner-select">
            {canPickOwners ? (
              <div className="pr-owner-picker" ref={ownerPickerRef}>
                <button
                  type="button"
                  className={`pr-owner-trigger ${ownerPickerOpen ? 'open' : ''}`}
                  aria-expanded={ownerPickerOpen}
                  onClick={() => setOwnerPickerOpen(o => !o)}
                  title="Choose which owners' folders to show"
                >
                  <span className="pr-owner-trigger-label">Owners</span>
                  <span className={`pr-owner-trigger-val ${selectedDocOwnerIds.length === 0 ? 'all' : ''}`}>
                    {selectedDocOwnerIds.length === 0
                      ? 'All'
                      : selectedDocOwnerIds.length === 1
                        ? (ownerEntities.find(e => `${e.owner_type}:${e.owner_id}` === selectedDocOwnerIds[0])?.display_name || '1 owner')
                        : `${selectedDocOwnerIds.length} owners`}
                  </span>
                  <FiChevronDown size={14} className={`pr-owner-trigger-caret ${ownerPickerOpen ? 'open' : ''}`} />
                </button>
                {ownerPickerOpen && (
                  <div className="pr-owner-pop">
                    <div className="pr-owner-pop-search">
                      <FiSearch size={12} />
                      <input
                        autoFocus
                        type="text"
                        value={ownerFilterText}
                        onChange={e => setOwnerFilterText(e.target.value)}
                        placeholder="Search owners…"
                      />
                      {selectedDocOwnerIds.length > 0 && (
                        <button
                          className="pr-owner-pop-clear"
                          onClick={() => { setSelectedDocOwnerIds([]); setSelectedDocId(null); }}
                          title="Show all owners"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="pr-owner-pop-list">
                      {(() => {
                        const q = ownerFilterText.trim().toLowerCase();
                        const list = q
                          ? pickableOwnerEntities.filter(e => e.display_name.toLowerCase().includes(q))
                          : pickableOwnerEntities;
                        if (list.length === 0) return <div className="pr-owner-pop-empty">No owners match.</div>;
                        return list.map(ent => {
                          const key = `${ent.owner_type}:${ent.owner_id}`;
                          const checked = selectedDocOwnerIds.includes(key);
                          return (
                            <button
                              key={key}
                              type="button"
                              className={`pr-owner-chip ${checked ? 'checked' : ''}`}
                              aria-pressed={checked}
                              onClick={() => toggleOwner(key)}
                              title={checked ? 'Click to remove from view' : 'Click to add to view'}
                            >
                              <span className="pr-owner-chip-check">{checked && <FiCheck size={12} />}</span>
                              <span className="pr-owner-chip-name">{ent.display_name}</span>
                              <span className="pr-owner-chip-badge">{ent.badge}</span>
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="pr-owner-self">Showing your folders</div>
            )}
            {/* New folder lives here as a compact "+" beside the Owners control. */}
            <button className="pr-owner-add-folder" onClick={openManageFolders} title="New folder">
              <FiPlus size={16} />
            </button>
          </div>
          <div className="pr-folder-search">
            <FiSearch size={13} />
            <input
              type="text"
              placeholder="Search folders..."
              value={folderSearchTerm}
              onChange={e => setFolderSearchTerm(e.target.value)}
              className="pr-folder-search-input"
            />
            {folderSearchTerm && (
              <button className="pr-folder-search-clear" onClick={() => setFolderSearchTerm('')}><FiX size={12} /></button>
            )}
          </div>
          <div className="pr-folder-tree">
            {rootFolders.length === 0 ? (
              <div className="pr-doc-list-empty">
                {scopedOwnerKeys.length > 0 ? 'No folders for the selected owner(s).' : 'No folders yet'}
              </div>
            ) : (
              rootFolders.map(f => renderFolderNode(f))
            )}
          </div>
        </div>
      )}

      {/* In folders-only mode (DMS → Folders → Folder List) the doc list / preview /
          forms are hidden — only the Record Room above is shown, full width. */}
      {!foldersOnly && (<>
      {/* ======== Left Panel — Doc List ======== */}
      <div
        className={`pr-left-panel ${showDocList ? '' : 'pr-left-collapsed'}`}
        style={showDocList ? { width: docListWidth, minWidth: docListWidth, maxWidth: docListWidth } : undefined}
      >
        {!showDocList ? (
          <button
            className="pr-doclist-expand"
            onClick={() => setShowDocList(true)}
            title="Show document list"
            aria-label="Show document list"
          >
            <FiChevronsRight size={16} />
            <span className="pr-doclist-expand-label">Documents</span>
          </button>
        ) : (<>
        {/* Owner scope lives in the Record Room multi-select. When the Record
            Room is collapsed, show a compact summary chip here so the active
            owner filter stays visible and clearable. */}

        {/* Active filter chips */}
        {((!showRecordRoom && canPickOwners && selectedDocOwnerIds.length > 0) || selectedRecordFolderId !== null) && (
          <div className="pr-active-filters">
            {!showRecordRoom && canPickOwners && selectedDocOwnerIds.length > 0 && (
              <span className="pr-filter-chip">
                <FiFolder size={12} />
                {selectedDocOwnerIds.length === 1
                  ? (ownerEntities.find(e => `${e.owner_type}:${e.owner_id}` === selectedDocOwnerIds[0])?.display_name || 'Owner')
                  : `${selectedDocOwnerIds.length} owners`}
                <button className="pr-filter-chip-clear" onClick={() => { setSelectedDocOwnerIds([]); setSelectedDocId(null); }} title="Clear owner filter"><FiX size={12} /></button>
              </span>
            )}
            {selectedRecordFolderId !== null && (
              <span className="pr-filter-chip">
                <FiFolder size={12} />
                {folders.find(f => f.folder_id === selectedRecordFolderId)?.folder_name || 'Folder'}
                <button className="pr-filter-chip-clear" onClick={() => { setSelectedRecordFolderId(null); setSelectedDocId(null); }} title="Clear folder filter"><FiX size={12} /></button>
              </span>
            )}
          </div>
        )}

        {/* Filter tabs — with a compact Record Room (folders) collapse toggle on the far left */}
        <div className="pr-filter-tabs">
          <button
            className={`pr-rr-collapse${!showRecordRoom ? ' engaged' : ''}`}
            onClick={() => setShowRecordRoom(s => !s)}
            title={showRecordRoom ? 'Hide Record Room (folders)' : 'Show Record Room (folders)'}
            aria-label="Toggle Record Room folders"
          >
            {showRecordRoom ? <FiChevronsLeft size={15} /> : <FiChevronsRight size={15} />}
          </button>
          {/* BK Done is a bookkeeping state — hidden in the DMS pillar (no bookkeeping),
              but kept under My Accounts / Client's Accounts where filing → bookkeeping happens. */}
          {FILTER_TABS.filter(tab => showBookkeeping || tab.key !== 'bk-done').map(tab => (
            <button
              key={tab.key}
              className={`pr-filter-tab ${activeFilter === tab.key ? 'active' : ''}`}
              onClick={() => { setActiveFilter(tab.key); setSelectedDocId(null); }}
            >
              {tab.label}
              <span className="pr-filter-count">({filterCounts[tab.key]})</span>
            </button>
          ))}
          {/* Collapse the whole document list pane → more room for the preview */}
          <button
            className="pr-doclist-collapse"
            onClick={() => setShowDocList(false)}
            title="Collapse the document list (more room for the preview)"
            aria-label="Collapse document list"
          >
            <FiChevronsLeft size={15} />
          </button>
        </div>

        {/* Search (≈75%) + Sort chevron (≈15%) on one line */}
        <div className="pr-list-controls">
          <div className="pr-search-wrapper">
            <FiSearch className="pr-search-icon" />
            <input
              type="text"
              className="pr-search-input"
              placeholder="Search filename..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="pr-sort-picker" ref={sortPickerRef}>
            <button
              type="button"
              className={`pr-sort-trigger ${sortOpen ? 'open' : ''}`}
              aria-expanded={sortOpen}
              aria-haspopup="listbox"
              onClick={() => setSortOpen(o => !o)}
              title={`Sort by: ${SORT_OPTIONS.find(o => o.value === sortKey)?.label || ''}`}
            >
              <FiChevronDown size={16} className={`pr-sort-caret ${sortOpen ? 'open' : ''}`} />
            </button>
            {sortOpen && (
              <div className="pr-sort-pop" role="listbox" aria-label="Sort documents by">
                <div className="pr-sort-pop-head">Sort by</div>
                {SORT_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={sortKey === o.value}
                    className={`pr-sort-opt ${sortKey === o.value ? 'active' : ''}`}
                    onClick={() => { setSortKey(o.value); setSortOpen(false); }}
                  >
                    <FiCheck size={13} className="pr-sort-opt-check" />
                    <span>{o.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Document list */}
        <div
          className="pr-doc-list"
          ref={docListRef}
          tabIndex={0}
          onKeyDown={handleListKeyDown}
          role="listbox"
          aria-label="Documents — use up and down arrows to navigate, Tab to view"
        >
          {filteredDocs.length === 0 ? (
            <div className="pr-doc-list-empty">
              {documents.filter(d => d.is_deleted !== 1).length === 0
                ? 'No documents uploaded yet. Upload documents first.'
                : 'No documents match this filter.'
              }
            </div>
          ) : (
            filteredDocs.map(doc => {
              // Owner badge reflects the CHOSEN owner only (owner_type/owner_id). If no
              // owner was picked at upload, leave it blank — do NOT fall back to the
              // uploader's identity (user_id), since uploader ≠ owner.
              const docNick = doc.owner_type && doc.owner_id
                ? getOwnerNickname(doc.owner_type, doc.owner_id, usersData, businessesData)
                : '';
              const isPerm = Number(doc.is_permanent) === 1;
              // Filed = the doc has a real folder anywhere — its primary folder OR any
              // shared owner's folder (dms_document_owners). Treat null / '' / 0 as none.
              const sharedFolderRows = docOwnersByDoc.get(doc.document_id) || [];
              const allFolderIds = [doc.dms_folders_id, ...sharedFolderRows.map(r => r.folder_id)]
                .filter(x => x != null && Number(x) !== 0)
                .map(Number);
              const isUnfiled = allFolderIds.length === 0;
              const folderNameOf = (id: number) =>
                folders.find(f => Number(f.folder_id) === id)?.folder_name || 'Unknown folder';
              // Prefer the folder that put this doc in the currently-filtered subtree,
              // else its primary folder, else the first shared folder.
              let shownFolderId: number | null = doc.dms_folders_id != null && Number(doc.dms_folders_id) !== 0
                ? Number(doc.dms_folders_id)
                : (allFolderIds[0] ?? null);
              if (selectedRecordFolderId !== null) {
                const sub = getFolderAndDescendantIds(selectedRecordFolderId);
                const hit = allFolderIds.find(id => sub.has(id));
                if (hit != null) shownFolderId = hit;
              }
              const docFolderName = shownFolderId != null ? folderNameOf(shownFolderId) : '';
              const extraFolderCount = allFolderIds.filter(id => id !== shownFolderId).length;
              const dup = dupInfo.get(doc.document_id);
              const isDupCopy = !!dup && !dup.isOriginal;
              // In the Duplicates sort, nest copies under their original (indent +
              // accent) so a group reads as one block even with different filenames.
              const dupRowClass = sortKey === 'duplicates' && dup
                ? (dup.isOriginal ? 'pr-dup-orig-row' : 'pr-dup-copy-row')
                : '';
              return (
                <div
                  key={doc.document_id}
                  data-doc-id={doc.document_id}
                  role="option"
                  aria-selected={selectedDocId === doc.document_id}
                  className={`pr-doc-item ${selectedDocId === doc.document_id ? 'selected' : ''} ${dupRowClass}`}
                  onClick={() => setSelectedDocId(doc.document_id)}
                >
                  {getStatusIcon(doc.status)}
                  <div className="pr-doc-info">
                    <div className="pr-doc-filename">
                      <span className={isDupCopy ? 'pr-dup-copy-name' : ''}>{doc.filename}</span>
                      {dup && (dup.isOriginal ? (
                        <span className="pr-dup-badge pr-dup-original" title="Original — other copies of this document exist">
                          Original
                        </span>
                      ) : (
                        <span className="pr-dup-badge pr-dup-copy" title={`Possible duplicate — ${dup.matchPct}% match with the original`}>
                          Dup {dup.matchPct}%
                        </span>
                      ))}
                    </div>
                    {/* Owner nickname + the folder it's filed in (blank when unfiled) */}
                    {(docNick || !isUnfiled) && (
                      <div className="pr-doc-owner-label">
                        {docNick && <span className="pr-doc-nick">{docNick}</span>}
                        {docNick && !isUnfiled && <span className="pr-doc-folder-sep">|</span>}
                        {!isUnfiled && (
                          <span className="pr-doc-folder-name">
                            {docFolderName}{extraFolderCount > 0 ? ` +${extraFolderCount}` : ''}
                          </span>
                        )}
                      </div>
                    )}
                    {showBookkeeping && txnCountByDoc.get(doc.document_id) ? (
                      <div className="pr-doc-txn-badge">💳 {txnCountByDoc.get(doc.document_id)} txns</div>
                    ) : null}
                  </div>
                  {/* Per-row quick actions (hover) */}
                  <div className="pr-doc-row-actions">
                    <button
                      className={`pr-doc-row-btn ${isPerm ? 'pr-star-on' : ''}`}
                      title={isPerm ? 'Permanent — click to unmark' : 'Mark permanent (never auto-delete)'}
                      onClick={(e) => { e.stopPropagation(); handleTogglePermanent(doc); }}
                    >
                      <FiStar size={13} style={isPerm ? { fill: 'currentColor' } : undefined} />
                    </button>
                    <button
                      className="pr-doc-row-btn"
                      title="Move to another folder"
                      onClick={(e) => { e.stopPropagation(); setSelectedDocId(doc.document_id); setShowDocMove(true); }}
                    >
                      <FiFolderPlus size={13} />
                    </button>
                    <button
                      className="pr-doc-row-btn pr-doc-row-danger"
                      title="Delete document"
                      onClick={(e) => { e.stopPropagation(); handleDeleteDoc(doc); }}
                    >
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        </>)}
        {/* Drag the right edge to widen/narrow the doc list; double-click to reset. */}
        {showDocList && (
          <div
            className="pr-doclist-resizer"
            onMouseDown={onDocListResizeStart}
            onDoubleClick={resetDocListWidth}
            title="Drag to resize the document list (double-click to reset)"
            role="separator"
            aria-orientation="vertical"
          />
        )}
      </div>

      {/* ======== Center Panel — Preview ======== */}
      <div
        className="pr-center-panel"
        ref={centerPaneRef}
        tabIndex={0}
        onKeyDown={handleViewerKeyDown}
        aria-label="Document viewer — left/right arrows change document, up/down and Page keys scroll"
      >
        {successMsg && <div className="pr-success-msg">{successMsg}</div>}
        {errorMsg && (
          <div className="pr-error-msg">
            {errorMsg}
            <button className="pr-error-msg-close" onClick={() => setErrorMsg('')} title="Dismiss">✕</button>
          </div>
        )}
        {extracting && (
          <div className="pr-extracting-banner">
            <span className="pr-extracting-spinner" /> Reading document &amp; auto-filling fields...
          </div>
        )}

        {/* Duplicate flag for the open document */}
        {selectedDoc && (() => {
          const dup = dupInfo.get(selectedDoc.document_id);
          if (!dup) return null;
          const groupCount = Array.from(dupInfo.values()).filter(g => g.groupId === dup.groupId).length;
          if (dup.isOriginal) {
            return (
              <div className="pr-dup-banner pr-dup-banner-original">
                <span className="pr-dup-banner-icon">⧉</span>
                <span>
                  This is the <strong>original</strong> — {groupCount - 1} other{groupCount - 1 !== 1 ? ' copies' : ' copy'} of this
                  document {groupCount - 1 !== 1 ? 'were' : 'was'} uploaded. Use the <em>Duplicates (grouped)</em> sort to see them all.
                </span>
              </div>
            );
          }
          const original = documents.find(d => d.document_id === dup.originalId);
          return (
            <div className="pr-dup-banner pr-dup-banner-copy">
              <span className="pr-dup-banner-icon">⚠️</span>
              <span>
                Possibly this is a <strong>duplicate</strong>. Sort by <em>Duplicate (Grouped)</em> to see both together.
              </span>
              {original && (
                <button
                  type="button"
                  className="pr-dup-view-original"
                  onClick={() => setSelectedDocId(dup.originalId as number)}
                >
                  View original
                </button>
              )}
            </div>
          );
        })()}

        <div className="pr-preview-nav">
          {/* Left zone: PDF view controls (merged here so there's one toolbar row). */}
          <div className="pr-nav-viewer">
            {pdfBytes && selectedDoc && (
              <>
                <button
                  className="pr-zoom-icon-btn"
                  title="Zoom out"
                  onClick={() => setPdfZoom(z => Math.max(0.25, +(z - 0.15).toFixed(2)))}
                >
                  <FiZoomOut size={15} />
                </button>
                <div className="pr-vmode-dd" ref={pdfModeRef}>
                  <button
                    className="pr-vmode-trigger"
                    onClick={() => setPdfModeMenuOpen(o => !o)}
                    title="Page view"
                    aria-haspopup="listbox"
                    aria-expanded={pdfModeMenuOpen}
                  >
                    <span className="pr-vmode-trigger-label">
                      {(VIEW_MODES.find(m => m.key === pdfViewMode) || VIEW_MODES[0]).label}
                    </span>
                    <FiChevronDown size={14} className={`pr-vmode-caret ${pdfModeMenuOpen ? 'open' : ''}`} />
                  </button>
                  {pdfModeMenuOpen && (
                    <div className="pr-vmode-menu" role="listbox" aria-label="Page view">
                      {VIEW_MODES.map(m => (
                        <button
                          key={m.key}
                          role="option"
                          aria-selected={pdfViewMode === m.key}
                          className={`pr-vmode-item ${pdfViewMode === m.key ? 'active' : ''}`}
                          title={m.title}
                          onClick={() => pickPdfMode(m.key)}
                        >
                          <FiCheck size={12} className="pr-vmode-item-check" />
                          {m.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className="pr-zoom-icon-btn"
                  title="Zoom in"
                  onClick={() => setPdfZoom(z => Math.min(4, +(z + 0.15).toFixed(2)))}
                >
                  <FiZoomIn size={15} />
                </button>
                {pdfNumPages > 1 && (
                  <span className="pr-pdf-pageinfo">Pg {pdfCurrentPage}/{pdfNumPages}</span>
                )}
              </>
            )}
          </div>
          <div className="pr-nav-pager">
            <button
              className="pr-nav-btn"
              disabled={currentIndex <= 0}
              onClick={() => navigateDoc(-1)}
              title="Previous document"
            >
              &lt;&lt;
            </button>
            <button
              className="pr-nav-btn"
              disabled={currentIndex >= filteredDocs.length - 1}
              onClick={() => navigateDoc(1)}
              title="Next document"
            >
              &gt;&gt;
            </button>
          </div>
          <div className="pr-nav-doc-actions">
            <button
              className="pr-nav-icon-btn"
              disabled={!pdfBytes || !selectedDoc}
              onClick={handleDownloadFile}
              title="Download this file"
            >
              <FiDownload size={15} />
            </button>
            <button
              className="pr-nav-icon-btn"
              disabled={!pdfBytes}
              onClick={handleOpenInNewTab}
              title="Open in new tab"
            >
              <FiExternalLink size={15} />
            </button>

            {/* ★ Permanent — never auto-delete */}
            <button
              className={`pr-nav-icon-btn ${Number(selectedDoc?.is_permanent) === 1 ? 'pr-star-on' : ''}`}
              disabled={!selectedDoc}
              onClick={() => handleTogglePermanent()}
              title={Number(selectedDoc?.is_permanent) === 1 ? 'Permanent (never auto-deleted) — click to unmark' : 'Mark permanent — never auto-delete'}
            >
              <FiStar size={15} style={Number(selectedDoc?.is_permanent) === 1 ? { fill: 'currentColor' } : undefined} />
            </button>

            {/* Move-to-folder popover anchor (the toolbar button was removed; the
                row-hover Move button in the doc list opens this same popover). */}
            <div className="pr-doc-move-wrap" ref={docMoveRef}>
              {showDocMove && selectedDoc && (
                <div className="pr-doc-move-pop">
                  <div className="pr-doc-move-search">
                    <FiSearch size={12} />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Move to folder…"
                      value={docMoveSearch}
                      onChange={e => setDocMoveSearch(e.target.value)}
                    />
                  </div>
                  <div className="pr-doc-move-list">
                    {(() => {
                      const q = docMoveSearch.trim().toLowerCase();
                      const targets = ownerFolders
                        .filter(f => Number(f.umbrella_folder) !== 1 && f.folder_id !== selectedDoc.dms_folders_id)
                        .filter(f => !q || f.folder_name.toLowerCase().includes(q))
                        .slice(0, 50);
                      if (targets.length === 0) return <div className="pr-doc-move-empty">No matching folders.</div>;
                      return targets.map(f => (
                        <button key={f.folder_id} className="pr-doc-move-item" onClick={() => handleMoveDoc(f.folder_id)}>
                          {f.folder_name}
                        </button>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Delete (soft-delete) */}
            <button
              className="pr-nav-icon-btn pr-nav-icon-danger"
              disabled={!selectedDoc}
              onClick={() => handleDeleteDoc()}
              title="Delete this document"
            >
              <FiTrash2 size={15} />
            </button>
          </div>
        </div>

        <div className="pr-preview-area">
          {!selectedDoc ? (
            <div className="pr-preview-placeholder">
              <FiFile style={{ fontSize: 48, color: 'var(--gray-300)' }} />
              <p>Select a document from the list to preview</p>
            </div>
          ) : pdfLoading ? (
            <div className="pr-preview-placeholder">
              <p>Loading preview...</p>
            </div>
          ) : pdfBytes ? (
            <PdfCanvasViewer
              pdfBytes={pdfBytes}
              title={selectedDoc.filename}
              viewMode={pdfViewMode}
              zoom={pdfZoom}
              onNumPages={setPdfNumPages}
              onCurrentPageChange={setPdfCurrentPage}
            />
          ) : (
            <div className="pr-preview-fallback">
              <div className="pr-preview-fallback-icon">
                <FiFileText />
              </div>
              <p><strong>{selectedDoc.filename}</strong></p>
              <p>{selectedDoc.page_count ? `${selectedDoc.page_count} page(s)` : 'PDF document'}</p>
            </div>
          )}
        </div>
      </div>

      {/* ======== Right Panel — Filing / Bookkeeping ======== */}
      <div className={`pr-right-panel ${showRightPanel ? '' : 'pr-right-collapsed'}`}>
        {/* Collapsed → a slim strip to re-open (mirrors the doc-list collapse). */}
        <button
          className="pr-right-expand"
          onClick={() => setShowRightPanel(true)}
          title="Show Filing panel"
          aria-label="Show Filing panel"
        >
          <FiChevronsLeft size={16} />
          <span className="pr-right-expand-label">Filing</span>
        </button>
        <div className="pr-right-tabs">
          <button
            className={`pr-right-collapse${!showRightPanel ? ' engaged' : ''}`}
            onClick={() => setShowRightPanel(false)}
            title="Collapse Filing panel"
            aria-label="Collapse Filing panel"
          >
            <FiChevronsRight size={15} />
          </button>
          <button
            className={`pr-right-tab ${rightTab === 'filing' ? 'active' : ''}`}
            onClick={() => switchRightTab('filing')}
          >
            Filing
          </button>
          {showBookkeeping && (
            <button
              className={`pr-right-tab ${rightTab === 'bookkeeping' ? 'active' : ''}`}
              onClick={() => switchRightTab('bookkeeping')}
            >
              Bookkeeping
            </button>
          )}
          {showBookkeeping && (
            <button
              className={`pr-right-tab ${rightTab === 'makejournal' ? 'active' : ''}`}
              onClick={() => switchRightTab('makejournal')}
            >
              Make Journal Entries
            </button>
          )}
          {showBookkeeping && docBankTxns.length > 0 && (
            <button
              className={`pr-right-tab ${rightTab === 'banktxn' ? 'active' : ''}`}
              onClick={() => switchRightTab('banktxn')}
            >
              Bank Txns <span className="pr-tab-badge">{docBankTxns.length}</span>
            </button>
          )}
        </div>

        {(showBookkeeping && rightTab === 'makejournal') ? (
          /* ---- Make Journal Entries (manual double-entry) ---- */
          <JournalEntriesTab
            viewMode={viewMode}
            selectedClientId={selectedClientId}
            documentId={selectedDoc ? selectedDoc.document_id : null}
            defaultOwnerKey={selectedDoc && selectedDoc.owner_type && selectedDoc.owner_id != null
              ? `${selectedDoc.owner_type}:${selectedDoc.owner_id}` : null}
          />
        ) : (showBookkeeping && rightTab === 'banktxn' && docBankTxns.length > 0) ? (
          /* ---- Bank Transactions (line-items from a statement) ---- */
          <>
            <div className="pr-banktxn-head">
              <div className="pr-banktxn-title">Bank Transactions</div>
              <div className="pr-banktxn-sub">
                {docBankTxns.length} line-item{docBankTxns.length === 1 ? '' : 's'} from this statement — assign a GL account to each, then post to the ledger.
              </div>
              <button type="button" className="pr-banktxn-bulk" onClick={markAllTxnsReviewed}>
                Mark all reviewed
              </button>
            </div>
            {/* Bank/cash account for this statement — the offsetting side of every posted txn */}
            <div className="pr-banktxn-post-bar">
              <label className="pr-banktxn-post-label">Bank / Cash account</label>
              <SearchableDropdown
                className="pr-banktxn-bankacct"
                options={coaOptions}
                value={bankAccountCoaId}
                onChange={(v: string) => setBankAccountCoaId(v)}
                placeholder={coaOptions.length ? 'Choose the statement’s bank account…' : 'No GL accounts yet'}
              />
              <button
                className="pr-submit-btn publish pr-banktxn-post-btn"
                disabled={txnBusy || !bankAccountCoaId}
                onClick={handlePostToGl}
                title={!bankAccountCoaId ? 'Pick the bank/cash account first' : 'Create GL journal entries for mapped, unposted transactions'}
              >
                {txnBusy ? 'Posting…' : 'Post to GL'}
              </button>
            </div>
            <div className="pr-banktxn-list">
              {docBankTxns.map(t => {
                const draft = txnDrafts.find(d => d.txn_id === t.txn_id);
                const amt = t.amount == null ? 0 : Number(t.amount);
                const isPosted = postedTxnIds.has(t.txn_id) || t.status === 'posted';
                return (
                  <div key={t.txn_id} className={`pr-banktxn-card ${t.direction} ${isPosted ? 'posted' : ''}`}>
                    <div className="pr-banktxn-row1">
                      <span className="pr-banktxn-date">{(t.txn_date || '').slice(0, 10) || '—'}</span>
                      {isPosted && <span className="pr-banktxn-posted-badge">Posted ✓</span>}
                      <span className={`pr-banktxn-amt ${t.direction}`}>
                        {t.direction === 'debit' ? '−' : '+'}{amt.toFixed(2)} {t.currency || ''}
                      </span>
                    </div>
                    <div className="pr-banktxn-desc" title={t.description || ''}>
                      {t.description || '(no description)'}
                    </div>
                    <div className="pr-banktxn-fields">
                      <SearchableDropdown
                        className="pr-banktxn-coa"
                        options={coaOptions}
                        value={draft?.coa_id != null ? String(draft.coa_id) : ''}
                        onChange={(v: string) => updateTxnDraft(t.txn_id, { coa_id: v ? Number(v) : null })}
                        placeholder={coaOptions.length ? 'Map to GL account…' : 'No GL accounts yet — add some in Chart of Accounts'}
                        disabled={isPosted}
                      />
                      <div className="pr-banktxn-field-row">
                        <input
                          type="text"
                          className="pr-form-input"
                          placeholder="Category"
                          value={draft?.category || ''}
                          onChange={e => updateTxnDraft(t.txn_id, { category: e.target.value })}
                          disabled={isPosted}
                        />
                        {isPosted ? (
                          <span className="pr-form-input pr-banktxn-status pr-banktxn-status-locked">posted</span>
                        ) : (
                          <select
                            className="pr-form-input pr-banktxn-status"
                            value={draft?.status || 'imported'}
                            onChange={e => updateTxnDraft(t.txn_id, { status: e.target.value })}
                          >
                            {TXN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="pr-form-actions">
              <button
                className="pr-submit-btn publish"
                disabled={txnBusy}
                onClick={handleSaveBankTxns}
              >
                {txnBusy ? 'Saving…' : 'Save assignments'}
              </button>
            </div>
          </>
        ) : (!showBookkeeping || rightTab === 'filing') ? (
          /* ---- Filing Form ---- */
          <>
            <div className="pr-right-form pr-form-outlined">
              {/* Select folder(s) — MULTI-select. Click one or more folders; the doc
                  is filed in every selected folder (selected pinned to the top, ✓). */}
              <div className="pr-form-group">
                <label className="pr-form-label">
                  Select folder(s) <span className="required">*</span>
                </label>
                <SearchableDropdown
                  className="pr-form-select"
                  multiSelect
                  options={folderSelectOptions}
                  selectedValues={filingFolderIds.map(String)}
                  onToggleValue={(v: string) => toggleFilingFolder(Number(v))}
                  placeholder="Select folder(s) — click one or more"
                />
              </div>

              {/* Owner(s) Of Document — MULTI-select. Click one or more owners; the doc
                  is co-owned by all of them (selected pinned to the top, ✓). */}
              <div className="pr-form-group">
                <label className="pr-form-label">
                  Owner(s) Of Document <span className="required">*</span>
                </label>
                <SearchableDropdown
                  className="pr-form-select"
                  multiSelect
                  options={ownerSelectOptions}
                  selectedValues={filingOwnerKeys}
                  onToggleValue={toggleFilingOwner}
                  placeholder="Select owner(s) — click one or more"
                />
                {filingFolderIds.length > 0 && filingOwnerKeys.length === 0 && (
                  <p className="pr-owner-gate-hint">
                    Choose at least one document owner to enable filing.
                  </p>
                )}
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">
                  Date of document
                  {filingDateAmbiguous && (
                    <span className="pr-ai-suggested" title={`Ambiguous printed date "${filingRawDate}" — pick the correct reading`}>✨ confirm date</span>
                  )}
                </label>
                <input
                  type="date"
                  className="pr-form-input date-highlight"
                  value={filingDate}
                  onChange={e => { setFilingDate(e.target.value); setFilingDateAmbiguous(false); setFormDirty(true); }}
                />
                {filingDateAmbiguous && filingDateCandidates.length > 0 && (
                  <div className="pr-date-candidates">
                    <span className="pr-date-candidates-label">"{filingRawDate}" could be:</span>
                    {filingDateCandidates.map(c => (
                      <button
                        key={c.order}
                        type="button"
                        className="pr-date-candidate-btn"
                        onClick={() => {
                          setFilingDate(c.iso);
                          setFilingDateFormatId(c.formatId);
                          setFilingDateAmbiguous(false);
                          setFormDirty(true);
                        }}
                      >
                        {c.iso} <span className="pr-date-candidate-order">({c.order})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Nature of Document</label>
                <SearchableDropdown
                  className="pr-form-select"
                  options={docuTypes.filter(dt => dt.is_deleted !== 1).map(dt => ({
                    value: String(dt.docu_type_id),
                    label: dt.docu_type_name,
                  }))}
                  value={filingDocuTypeId}
                  onChange={(v: string) => { setFilingDocuTypeId(v); setFormDirty(true); }}
                  placeholder="Search and Select Document Type"
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Party Name</label>
                <input
                  type="text"
                  className="pr-form-input"
                  placeholder=""
                  value={filingPartyName}
                  onChange={e => { setFilingPartyName(e.target.value); setFormDirty(true); }}
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Invoice / Ref. #</label>
                <input
                  type="text"
                  className="pr-form-input"
                  value={filingInvoiceRef}
                  onChange={e => { setFilingInvoiceRef(e.target.value); setFormDirty(true); }}
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Method of payment if any</label>
                <SearchableDropdown
                  className="pr-form-select"
                  options={paymentMethods.filter(p => Number(p.is_deleted) !== 1).map(p => ({
                    value: String(p.payment_method_id),
                    label: p.method_name,
                  }))}
                  value={filingPaymentMethodId}
                  onChange={(v: string) => {
                    setFilingPaymentMethodId(v);
                    const pm = paymentMethods.find(p => String(p.payment_method_id) === v);
                    setFilingPaymentMethod(pm ? pm.method_name : '');  // keep legacy text in sync
                    setFormDirty(true);
                  }}
                  placeholder="Select payment method"
                />
              </div>

              <div className="pr-form-row">
                <div className="pr-form-group">
                  <label className="pr-form-label">Tax{taxOptionInfo.ownerState ? ` (${taxOptionInfo.ownerState})` : ''}</label>
                  <SearchableDropdown
                    className="pr-form-select"
                    options={taxOptionInfo.options}
                    value={filingTaxRateId}
                    onChange={(v: string) => {
                      setFilingTaxRateId(v);
                      const rate = taxRates.find(r => String(r.tax_rate_id) === v);
                      setFilingTaxTypeId(rate ? String(rate.tax_type_id) : '');
                      setFormDirty(true);
                    }}
                    placeholder="Select tax (optional)"
                  />
                  {taxOptionInfo.needsAddress && (
                    <p className="pr-tax-addr-hint">
                      Showing federal tax only. Add this owner's province in <strong>Family &amp; Businesses</strong> to see province-specific taxes.
                    </p>
                  )}
                  {taxOwnerAddress.asOfMoved && (
                    <p className="pr-tax-addr-hint" style={{ color: 'var(--gray-500)' }}>
                      Using <strong>{taxOptionInfo.ownerState}</strong> — where this business was located on the document's date.
                    </p>
                  )}
                </div>
                <div className="pr-form-group">
                  <label className="pr-form-label">Tax amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="pr-form-input"
                    value={filingTaxAmount}
                    onChange={e => { setFilingTaxAmount(e.target.value); setFormDirty(true); }}
                  />
                </div>
              </div>

              <div className="pr-form-row">
                <div className="pr-form-group">
                  <label className="pr-form-label">Total amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="pr-form-input"
                    value={filingAmount}
                    onChange={e => { setFilingAmount(e.target.value); setFormDirty(true); }}
                  />
                </div>
                <div className="pr-form-group">
                  <label className="pr-form-label">Currency</label>
                  <input
                    type="text"
                    className="pr-form-input"
                    placeholder="CAD"
                    maxLength={3}
                    value={filingCurrency}
                    onChange={e => { setFilingCurrency(e.target.value.toUpperCase()); setFormDirty(true); }}
                  />
                </div>
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Purpose / Content of Document</label>
                <textarea
                  className="pr-form-textarea"
                  value={filingPurpose}
                  onChange={e => { setFilingPurpose(e.target.value); setFormDirty(true); }}
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Rename Document</label>
                <input
                  type="text"
                  className="pr-form-input"
                  value={filingFilename}
                  placeholder="Document name"
                  onChange={e => {
                    const v = e.target.value;
                    setFilingFilename(v);
                    // Real-time save: debounce while typing so PostgreSQL is updated
                    // as the user edits (without a write per keystroke).
                    if (renameTimerRef.current) clearTimeout(renameTimerRef.current);
                    if (selectedDoc) {
                      const id = selectedDoc.document_id;
                      renameTimerRef.current = setTimeout(() => persistRename(id, v), 600);
                    }
                  }}
                  onBlur={() => {
                    if (renameTimerRef.current) clearTimeout(renameTimerRef.current);
                    if (selectedDoc) persistRename(selectedDoc.document_id, filingFilename);
                  }}
                />
                <span className="pr-form-hint">Saved automatically as you type.</span>
              </div>
            </div>

            <div className="pr-form-actions">
              <button
                className="pr-reject-btn"
                onClick={handleReject}
                title="Reject this document"
              >
                Reject
              </button>
              <button
                className="pr-submit-btn"
                disabled={filingOwnerKeys.length === 0 || filingFolderIds.length === 0}
                onClick={handleFilingSubmit}
              >
                Submit
              </button>
            </div>
          </>
        ) : (
          /* ---- Bookkeeping Form ---- */
          <>
            <div className="pr-right-form pr-form-outlined">
              <div className="pr-form-group">
                <label className="pr-form-label">Owner Of Document <span className="required">*</span></label>
                <SearchableDropdown
                  className="pr-form-select"
                  options={ownerEntities.map(ent => ({
                    value: `${ent.owner_type}:${ent.owner_id}`,
                    label: ent.display_name,
                  }))}
                  value={bkOwnerKey}
                  onChange={setBkOwnerKey}
                  placeholder="Choose a Document Owner"
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Document Type</label>
                <SearchableDropdown
                  className="pr-form-select"
                  options={docuTypes.filter(dt => dt.is_deleted !== 1).map(dt => ({
                    value: String(dt.docu_type_id),
                    label: dt.docu_type_name,
                  }))}
                  value={bkDocuTypeId}
                  onChange={setBkDocuTypeId}
                  placeholder="Select type..."
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Party Name</label>
                <input
                  type="text"
                  className="pr-form-input"
                  value={bkPartyName}
                  onChange={e => setBkPartyName(e.target.value)}
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Invoice / Ref. #</label>
                <input
                  type="text"
                  className="pr-form-input"
                  value={bkInvoiceRef}
                  onChange={e => setBkInvoiceRef(e.target.value)}
                />
              </div>

              <div className="pr-form-row">
                <div className="pr-form-group">
                  <label className="pr-form-label">Date</label>
                  <input
                    type="date"
                    className="pr-form-input"
                    value={bkDate}
                    onChange={e => setBkDate(e.target.value)}
                  />
                </div>
                <div className="pr-form-group">
                  <label className="pr-form-label">Due Date</label>
                  <input
                    type="date"
                    className="pr-form-input"
                    value={bkDueDate}
                    onChange={e => setBkDueDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="pr-form-row">
                <div className="pr-form-group">
                  <label className="pr-form-label">Total Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="pr-form-input"
                    value={bkAmount}
                    onChange={e => setBkAmount(e.target.value)}
                  />
                </div>
                <div className="pr-form-group">
                  <label className="pr-form-label">Currency</label>
                  <input
                    type="text"
                    className="pr-form-input"
                    placeholder="USD"
                    maxLength={3}
                    value={bkCurrency}
                    onChange={e => setBkCurrency(e.target.value.toUpperCase())}
                  />
                </div>
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Tax Rate</label>
                <input
                  type="number"
                  step="0.01"
                  className="pr-form-input"
                  placeholder="e.g. 10.00"
                  value={bkTaxRate}
                  onChange={e => setBkTaxRate(e.target.value)}
                />
              </div>

              {/* Tax summary */}
              {(Number(bkAmount) > 0) && (
                <div className="pr-tax-summary">
                  <div className="pr-tax-line">
                    <span>Subtotal:</span>
                    <span>{bkComputed.subtotal.toFixed(2)} {bkCurrency}</span>
                  </div>
                  <div className="pr-tax-line">
                    <span>Tax:</span>
                    <span>{bkComputed.tax.toFixed(2)} {bkCurrency}</span>
                  </div>
                  <div className="pr-tax-line total">
                    <span>Total:</span>
                    <span>{bkComputed.total.toFixed(2)} {bkCurrency}</span>
                  </div>
                </div>
              )}

              <div className="pr-form-group">
                <label className="pr-form-label">Rename Document</label>
                <input
                  type="text"
                  className="pr-form-input"
                  value={filingFilename}
                  placeholder="Document name"
                  onChange={e => {
                    const v = e.target.value;
                    setFilingFilename(v);
                    if (renameTimerRef.current) clearTimeout(renameTimerRef.current);
                    if (selectedDoc) {
                      const id = selectedDoc.document_id;
                      renameTimerRef.current = setTimeout(() => persistRename(id, v), 600);
                    }
                  }}
                  onBlur={() => {
                    if (renameTimerRef.current) clearTimeout(renameTimerRef.current);
                    if (selectedDoc) persistRename(selectedDoc.document_id, filingFilename);
                  }}
                />
                <span className="pr-form-hint">Saved automatically as you type.</span>
              </div>
            </div>

            <div className="pr-form-actions">
              <button
                className="pr-submit-btn publish"
                disabled={!bkOwnerKey}
                onClick={handleBkPublish}
              >
                Publish
              </button>
            </div>
          </>
        )}
      </div>
      </>)}

      {/* ======== Manage Folders popup (full functionality: owner selection + validation) ======== */}
      {showManageFolders && (
        <div
          className="pr-mf-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeManageFolders(); }}
        >
          <div className="pr-mf-modal">
            <div className="pr-mf-modal-head">
              <span className="pr-mf-modal-title">Manage Folders</span>
              <button className="pr-mf-modal-close" onClick={closeManageFolders} title="Close">
                <FiX size={18} />
              </button>
            </div>
            <div className="pr-mf-modal-body">
              <ManageFolders viewMode={viewMode} selectedClientId={selectedClientId} />
            </div>
          </div>
        </div>
      )}

      {/* ---- Delete document confirmation ---- */}
      {deleteDocTarget && (
        <div
          className="pr-mf-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !deletingDoc) setDeleteDocTarget(null); }}
        >
          <div className="pr-del-modal" role="dialog" aria-modal="true">
            <div className="pr-del-head">
              <div className="pr-del-title"><FiAlertCircle size={18} /> Delete Document</div>
              <button
                className="pr-mf-modal-close"
                onClick={() => setDeleteDocTarget(null)}
                disabled={deletingDoc}
                title="Close"
              >
                <FiX size={18} />
              </button>
            </div>
            <div className="pr-del-body">
              <div className="pr-del-warning">
                <div className="pr-del-warning-title">
                  <FiAlertCircle size={16} /> Are you sure you want to delete &ldquo;{deleteDocTarget.filename}&rdquo;?
                </div>
                <p>This removes the document from your list. It is a soft-delete (reversible) — you can restore it from <strong>Reports → Deleted Items</strong>.</p>
              </div>
            </div>
            <div className="pr-del-actions">
              <button className="pr-del-btn-secondary" onClick={() => setDeleteDocTarget(null)} disabled={deletingDoc}>
                Cancel
              </button>
              <button className="pr-del-btn-danger" onClick={confirmDeleteDoc} disabled={deletingDoc}>
                <FiTrash2 size={16} /> {deletingDoc ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessReceiptsTab;
